// Modo "Variantes de acabado" — pantalla propia (ruta /variantes-de-acabado).
//
// El MISMO mueble, una imagen por material o color: el aparador en roble, en nogal, en
// terracota… Sólo para marcas del rubro Hogar y Decoración — `soloHogarDeco` en
// `modesCatalog` esconde la card, y esta pantalla además rebota a la home si alguien
// llega por URL. Ver ADR-2026-09-03-modo-variantes-de-acabado.
//
// POR QUÉ NO ES UNA VISTA DEL CATÁLOGO: allá la cantidad de imágenes es "producto ×
// vista". Acá es "foto de partida × acabado" —y las fotos no son productos del catálogo,
// sino creaciones o archivos del usuario—. Ese cálculo rompe el del Catálogo, así que el
// modo va aparte.
//
// Toma la anatomía de Modelo v2 / "Mostrar cómo funciona"
// (ADR-2026-08-21-modelo-v2-una-sola-columna):
//   • una COLUMNA de configuración a la izquierda, con las cards de 76px, el pie fijo
//     con los ajustes y el Generar, y el ancho elástico `ANCHO_PANEL_CONFIG`;
//   • lo que no se resuelve en la columna se elige en el PANEL LATERAL, que se abre
//     sobre el área de la derecha (productos, acabados y fondo, formato);
//   • a la derecha, la muestra del modo hasta que exista una tanda, y las tandas después.
//
// Dos pasos sobre la misma pantalla: (1) elegir las fotos de partida —la grilla a la
// vista, como en Automático—, (2) configurar la paleta. La columna es la misma en los
// dos: avanzar agrega lo que falta debajo de la card y cambia el CTA, que es lo que hace
// que se lea como un avance y no como otra pantalla.
//
// GENERAR llama al backend (ADR-2026-09-23-variantes-de-acabado-se-conecta-al-backend):
// sube a storage las fotos y muestras que vinieron como archivo, arma el body del
// contrato (`lib/variantesAcabado.ts`), dispara `POST /v1/generate/variantes-acabado`
// y sigue el job por `/v1/jobs/:id`. La tanda se dibuja ENSEGUIDA —una celda por par
// (foto, acabado)— y cada celda pasa a la imagen o a su error cuando el job termina: un
// par que falla no voltea a los demás, y lo que falla no se cobra.
//
// EN MOBILE son los MISMOS dos pasos en dos pantallas full-screen, con la anatomía de
// Modelo y de "Ambiente completo" (ADR-2026-09-10-ambiente-completo-en-el-telefono):
// (1) la grilla de creaciones con su CTA al pie, (2) "Elegir los acabados" con las mismas
// cards de la columna y el MISMO pie de desktop abajo. Lo que en desktop se elige en el
// panel lateral, acá se elige en una CAPA full-screen con el mismo contenido —Formato es
// la excepción, que tiene su bottom sheet de siempre—, y la card de las imágenes de
// partida no abre panel: devuelve al paso 1, que es donde vive la grilla.
// Ver ADR-2026-09-10-los-modos-de-deco-en-el-telefono.
//
// OJO con los colores: los tokens `brand-*` no declaran `<alpha-value>`, así que
// `text-brand-black/90` no emite regla (memoria del repo). Acá se usa `text-black/<n>`
// para los grises y `text-brand-black` pelado para el negro.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type LucideIcon,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  ImageUp,
  Palette,
  PencilLine,
  AlertTriangle,
  Sofa,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useSidebar } from '../../../context/SidebarContext';
import { useShellEscritorio } from '../../../hooks/useShellEscritorio';
import { useRubroMarcaActiva } from '../../../hooks/useRubroMarca';
import { esRubroHogarDeco } from '../../../lib/rubro';
import CachedImg from '../../../components/CachedImg';
import { EncabezadoPantalla } from '../../../components/PantallaChrome';
import { DEFAULT_FORMAT_ID, getFormatLabel } from '../mockData';
import { GeneradasPickGrid, type GeneradaPick } from './shared/GeneradasPickGrid';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { subirImagenParaAnimar } from '../../../lib/animarImagenUpload';
import {
  preloadGeneratedImages,
  type PreloadedImage,
} from '../../../utils/preloadGeneratedImages';
import {
  armarPedidoVariantes,
  esperarVariantes,
  postVariantesAcabado,
  textoDeErrorDePar,
  type ReferenciaSubida,
} from '../../../lib/variantesAcabado';
import { CardProductos } from '../modelo/PanelCompacto';
import { fetchUserProducts } from '../productsApi';
import LoadingAnimation from '../../../components/LoadingAnimation';
import { CapaModoMobile } from './shared/CapaModoMobile';
import { useColumnaResultadosModo } from './shared/useColumnaResultadosModo';
import { PanelLateralModo } from '../components/PanelLateralModo';
import { FormatPicker, FormatPickerGrid } from '../FormatPicker';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
import {
  CalidadSegmentada,
  CALIDAD_DEFAULT,
  CeldaAjuste,
  ValorConChevron,
  type CalidadImagen,
} from '../components/AjustesRapidosProducto';
import { AnimatedBlurBg } from '../AnimatedBlurBg';
import {
  ANCHO_PANEL_CONFIG,
  COLUMNA_RESULTADOS_AL_BORDE,
  FILA_MODO_REDISENO,
} from '../components/layoutModo';
import { nombreCortoModo } from '../modesCatalog';
import {
  ACABADOS,
  ACABADOS_POR_DEFECTO,
  CATEGORIAS_ACABADO,
  FONDOS,
  TRAMA_POR_CATEGORIA,
  costoTanda,
  fondoPorId,
  formatoDeRatioId,
  ratioDeFormato,
  type Acabado,
  type CategoriaAcabadoId,
  type FondoId,
} from '../variantesAcabado';

/** Segmento de ruta del modo — el mismo que `path` en `modesCatalog`. */
const PATH_MODO = 'variantes-de-acabado';

// El ancho del panel, el padding de la fila y los márgenes de la columna de resultados
// salen de `layoutModo`, que es donde viven para los modos del rediseño. Este modo tenía
// su propia copia del ancho y se había quedado en el valor VIEJO —660px contra los 600
// de hoy (ADR-2026-09-06-un-solo-ancho-de-panel-para-los-modos)—, así que abría con un
// panel más gordo que Automática; y su fila era el 90% centrado, que la dejaba lejos de
// los dos bordes en vez de a 52px (ADR-2026-09-24-variantes-usa-la-caja-de-los-otros-modos).

/** Ancho del panel lateral — 615px, el del diseño y el de los pickers de Modelo. */
const ANCHO_PANEL_LATERAL = 615;

/**
 * Aire entre la columna de configuración y el panel lateral, en px.
 *
 * Arrancaba pegado al borde de la columna y las dos cajas blancas se leían como una
 * sola —el panel parecía montado ENCIMA de la columna en vez de al lado—. Con el mismo
 * aire que separa las dos columnas (`gap-6`) se ve como lo que es: otra caja.
 */
const SEPARACION_PANEL = 24;

/** Columnas de la grilla de muestras del panel de acabados (las del diseño). */
const COLUMNAS_PALETA = 5;

/** Columnas de las grillas de tarjetas del panel lateral (fondo, formato). */
const COLUMNAS_PANEL_LATERAL = 3;

/**
 * Portada del modo mientras no hay tanda: el mismo mueble en varios acabados, que es
 * exactamente lo que el modo devuelve. Es PROPIA de esta pantalla — el packshot que había
 * antes lo comparten otras siete superficies, así que cambiarlo acá las cambiaba a todas.
 */
const PORTADA = '/images/modo_catalogo_muebles/hero_variantes.png';

/**
 * Lo que dice la MUESTRA del modo — qué hace, en una línea y un ejemplo.
 *
 * Vive acá arriba porque se dibuja en DOS lugares: la columna de la derecha en desktop y,
 * en el teléfono, arriba del paso 1 (ADR-2026-09-10-la-muestra-del-modo-tambien-en-mobile).
 */
/**
 * Cuánto dura el overlay de generación en el teléfono.
 *
 * Es el tiempo ESTIMADO: la barra se pacea con esto y quien cierra el overlay es
 * `completeNow` cuando llega la respuesta, como en Modelo y en Carrusel.
 */
const CARGA_BASE_MS = 25_000;
const CARGA_POR_IMAGEN_MS = 6_000;
/** Lo que tarda una tanda, para pacear la barra: ~30 s por imagen en paralelo. */
const cargaEstimadaMs = (imagenes: number): number =>
  Math.min(180_000, CARGA_BASE_MS + CARGA_POR_IMAGEN_MS * Math.max(1, imagenes));

/**
 * Cuántas miniaturas lleva la card de la pantalla de arranque, y con qué caer si el
 * catálogo no tiene ninguna. Son un EJEMPLO de lo que hay del otro lado —van atenuadas y
 * sin tilde—, no una selección.
 */
const MINIATURAS_CARD = 3;

/**
 * Cuánto se espera, como MUCHO, a la precarga de las imágenes del teléfono.
 *
 * `preloadGeneratedImages` espera un `onload`/`onerror` que no tiene timeout, así que una
 * imagen que nunca contesta la deja sin resolver. De esa precarga dependen DOS cosas —el
 * cierre de la tanda del store y el overlay de "Generando…"— y las dos se quedaban
 * colgadas con ella. Las imágenes ya están generadas y en Creaciones: lo único que se
 * espera acá es que el navegador las tenga a mano, así que pasado este tope se sigue
 * igual (ADR-2026-09-25-revision-greptile-la-tanda-no-cuelga-de-la-precarga).
 */
const ESPERA_MAX_PRECARGA_MS = 10_000;

/** La promesa, o el valor de respaldo si tarda más de `ms`. Nunca queda colgada. */
const conTope = <T,>(promesa: Promise<T>, ms: number, respaldo: T): Promise<T> =>
  Promise.race([
    promesa.catch(() => respaldo),
    new Promise<T>((res) => {
      window.setTimeout(() => res(respaldo), ms);
    }),
  ]);
const PLACEHOLDERS_MUEBLES = [
  '/images/vistas_muebles/packshot.png',
  '/images/modo_catalogo_muebles/hero_packshot.png',
];

const TITULO_MUESTRA = 'El mismo mueble, en toda tu paleta';
const BAJADA_MUESTRA = 'Una imagen por material o color, con la misma foto de partida';

/**
 * Una de las fotos que se van a repintar en cada acabado.
 *
 * NO es un producto del catálogo: el modo parte de una FOTO ya lograda —una creación del
 * usuario o un archivo suyo— y le cambia el material o el color manteniendo la forma y el
 * fondo. El packshot crudo del catálogo suele ser justo lo que NO se quiere repetir
 * ocho veces (ADR-2026-09-03-variantes-parte-de-una-imagen-no-de-un-producto).
 *
 * Se eligen VARIAS: el catálogo de una marca de muebles se rehace por temporada, y
 * repintar el aparador, la mesa y la biblioteca en la misma paleta era entrar tres veces
 * (ADR-2026-09-04-la-imagen-de-partida-puede-ser-mas-de-una).
 */
interface ImagenOrigen {
  /**
   * Clave de la selección. Para una creación es su `id`, que es lo que la grilla marca
   * con el tilde; para un archivo, una propia — dos fotos del escritorio pueden llamarse
   * igual y no tienen id.
   */
  key: string;
  /** `generada`: una creación del usuario. `subida`: un archivo de su computadora. */
  tipo: 'generada' | 'subida';
  /** Para dibujarla: signed URL de la creación, u `objectURL` del archivo. */
  url: string;
  /** Id de la fila de `imagenes_generadas` — sólo en `generada`, y es lo que viaja. */
  id?: string;
  /** El archivo — sólo en `subida`, y es lo que viajaría. */
  archivo?: File;
  nombre: string;
}

/**
 * Los pasos del modo.
 *
 * `presentacion` es SÓLO de mobile, con la misma anatomía que en "Ambiente completo"
 * (ADR-2026-09-10-ambiente-completo-abre-con-la-muestra): la muestra del modo arriba y
 * abajo el camino para traer imágenes. En desktop no existe — allá la muestra vive en la
 * columna de la derecha y se ve AL MISMO TIEMPO que la grilla, así que una pantalla
 * previa sería la misma información dos veces.
 *
 * `resultados` también es sólo de mobile: lo que sale de Generar vive en una pantalla
 * PROPIA, detrás del overlay de "Generando imágenes…", y no encima de los ajustes
 * (ADR-2026-09-10-variantes-mobile-genera-en-pantalla-completa). En desktop las tandas
 * caen en la columna de la derecha, que está al lado y no encima de nada.
 */
type Paso = 'presentacion' | 'producto' | 'configurar' | 'resultados';
type PanelLateral = 'origen' | 'acabados' | 'formato' | null;

/**
 * Una celda de la tanda: un par (foto, acabado). Nace `generando`; cuando el job termina
 * pasa a `lista` con su imagen o a `error` con un texto corto. El `imagenIndex` es el
 * índice de la foto en el body, que es como el backend nombra cada resultado.
 */
interface CeldaVariante {
  key: string;
  imagenIndex: number;
  origen: ImagenOrigen;
  acabado: Acabado;
  estado: 'generando' | 'lista' | 'error';
  url?: string;
  imagenId?: string;
  mensaje?: string;
}

/** Una tanda: lo que salió de un toque de Generar. */
interface TandaVariantes {
  id: string;
  ratio: number;
  /**
   * Una entrada por foto de partida, con LOS ACABADOS QUE LE TOCARON a esa foto y no a
   * la tanda entera (ADR-2026-09-04-cada-imagen-lleva-sus-acabados). Congelado: cambiar
   * la paleta después no reescribe lo ya generado.
   */
  porImagen: { origen: ImagenOrigen; acabados: Acabado[] }[];
  /** Las celdas, por foto y después por acabado: el orden en que se dibujan. */
  celdas: CeldaVariante[];
  /** El `execution_id` del job, apenas el backend lo acepta. */
  executionId?: string;
  /** Si la tanda ENTERA falló (ni una imagen), el motivo. */
  error?: string;
  creadaEn: number;
}

/**
 * Qué decir cuando el backend entregó ALGUNOS pares y falló otros.
 *
 * La columna compartida muestra lo que salió y nada más: su tanda se modela como "total +
 * las que llegaron", sin identidad por celda, así que un par que falla no tiene dónde
 * contarse (ADR-2026-09-25-revision-greptile-los-pares-que-fallan-se-cuentan). Sin esto,
 * el usuario pedía doce imágenes, veía diez y no se enteraba de nada.
 *
 * Nombra los acabados —que es lo que distingue un par de otro— y, cuando todos fallaron
 * por lo mismo, agrega el motivo una sola vez en vez de repetirlo.
 */
const resumenDeFallados = (
  fallados: { acabado: string; motivo: string }[],
  total: number,
): string => {
  const nombres = fallados.map((f) => f.acabado);
  const lista =
    nombres.length <= 2
      ? nombres.join(' y ')
      : `${nombres.slice(0, 2).join(', ')} y ${nombres.length - 2} más`;
  const motivos = new Set(fallados.map((f) => f.motivo));
  const motivo = motivos.size === 1 ? ` · ${[...motivos][0]}` : '';
  return fallados.length === 1
    ? `«${lista}» no salió${motivo}. Las otras ${total - 1} están a la derecha.`
    : `${fallados.length} de ${total} no salieron: ${lista}${motivo}.`;
};

/** Ícono de cada fondo, hasta que existan fotos de ejemplo. */
const ICONO_FONDO: Record<FondoId, LucideIcon> = {
  original: ImageIcon,
  estudio: Camera,
  ambiente: Sofa,
  describir: PencilLine,
};

/**
 * El relieve que se le pone encima a todas las muestras: un poco de luz arriba y sombra
 * abajo. Va aparte de la trama para que un color plano —los del eje `color`— también
 * se lea como una muestra física y no como un rectángulo de CSS.
 */
const RELIEVE_MUESTRA =
  'linear-gradient(160deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 45%, rgba(0,0,0,0.10) 100%)';

/**
 * La muestra de un acabado: el color, su trama y —si el acabado la tiene— la foto.
 * Sirve a los tres tamaños que aparecen en la pantalla (la grilla del panel, el mosaico
 * de la card y el chip de las pastillas), así que el tamaño lo pone quien la usa.
 */
const Muestra: React.FC<{ acabado: Acabado; className?: string }> = ({
  acabado,
  className = '',
}) => {
  // La suya primero: los ejes mezclan materiales y hay acabados —la piedra, la
  // esterilla— cuya trama no puede salir de la categoría.
  const trama = acabado.trama ?? TRAMA_POR_CATEGORIA[acabado.categoria];
  return (
    <span
      className={`relative block overflow-hidden ${className}`}
      style={{ background: acabado.color }}
    >
      {acabado.imageUrl && (
        <img
          src={acabado.imageUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {trama && !acabado.imageUrl && (
        <span className="absolute inset-0 block" style={{ background: trama }} />
      )}
      <span className="absolute inset-0 block" style={{ background: RELIEVE_MUESTRA }} />
    </span>
  );
};

/**
 * Card en FILA de 76px —miniatura cuadrada, etiqueta y valor, chevron— la misma forma que
 * las cards de Modelo, Pose y Fondo de la columna de Modelo v2
 * (ADR-2026-08-31-modelo-v2-cards-en-fila). Local y no importada por lo mismo que en
 * "Mostrar cómo funciona": la de `PanelCompacto` no se exporta.
 */
const CardFila: React.FC<{
  etiqueta: string;
  valor: string;
  miniatura: React.ReactNode;
  onClick: () => void;
  /** Resaltada: es la card cuyo panel está abierto. */
  activa?: boolean;
  /**
   * El segundo renglón va en primario y no en gris.
   *
   * Para cuando ese renglón no DESCRIBE lo elegido sino que INVITA a cambiarlo
   * ("Cambiar imágenes"): en gris se lee como un dato más de la card y nadie lo toca.
   */
  valorEnPrimario?: boolean;
}> = ({ etiqueta, valor, miniatura, onClick, activa = false, valorEnPrimario = false }) => (
  <button
    type="button"
    data-abre-panel-lateral
    onClick={onClick}
    // Fondo activo con hex+alpha, NO `bg-brand-primary/[0.06]`: `brand-primary` es una
    // `var()` con hex y el modificador de opacidad de Tailwind no emite regla (ADR-140),
    // así que la card activa se quedaba SIN fondo — blanca al lado de las grises.
    className={`w-full h-[76px] shrink-0 rounded-2xl p-3 flex items-center gap-3 text-left transition-colors ${
      activa
        ? 'bg-[#1644EB]/[0.06]'
        : 'bg-black/[0.035] hover:bg-black/[0.06]'
    }`}
  >
    <div className="relative h-full aspect-square shrink-0 rounded-xl overflow-hidden bg-white grid place-items-center">
      {miniatura}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[14px] font-bold text-brand-black leading-tight">{etiqueta}</p>
      <p
        className={`text-[13px] leading-tight mt-0.5 truncate ${
          valorEnPrimario ? 'text-brand-primary font-semibold' : 'text-black/50'
        }`}
      >
        {valor}
      </p>
    </div>
    <ChevronRight
      className={`w-[18px] h-[18px] shrink-0 ${activa ? 'text-brand-primary' : 'text-black/30'}`}
    />
  </button>
);

/**
 * El switch de "todas por igual / una por una". Segmentado y no un toggle: las dos
 * opciones necesitan NOMBRE —"apagado" no dice "cada foto lleva lo suyo"— y es el mismo
 * control que la app ya usa para Calidad (ADR-2026-09-04-un-switch-decide-si-la-paleta-es-una-o-una-por-foto).
 */
const SwitchConfigPorImagen: React.FC<{
  igual: boolean;
  onChange: (igual: boolean) => void;
}> = ({ igual, onChange }) => (
  <span className="flex items-center gap-0.5 p-0.5 rounded-lg bg-black/[0.05]">
    {/* "Una por una" va PRIMERA: es la opción por defecto
        (ADR-2026-09-04-el-switch-abre-en-una-por-una), y un segmentado se lee de
        izquierda a derecha — la marca arrancaba en la segunda posición. */}
    {[
      { v: false, label: 'Una por una' },
      { v: true, label: 'Todas por igual' },
    ].map((o) => (
      <button
        key={String(o.v)}
        type="button"
        aria-pressed={igual === o.v}
        onClick={() => onChange(o.v)}
        className={`h-7 px-3 rounded-md text-[12px] font-semibold whitespace-nowrap transition-colors ${
          igual === o.v
            ? 'bg-white text-brand-black/90 shadow-sm'
            : 'text-[#3C3C43]/55 hover:text-brand-black/80'
        }`}
      >
        {o.label}
      </button>
    ))}
  </span>
);

/**
 * El mosaico 2×2 de la card de Acabados: tres muestras y la cuarta celda en "+N".
 *
 * Con cuatro o menos entran todas y no hay "+N" que poner; con más, la cuarta celda
 * cuenta lo que no se ve. Es el mismo dibujo que el mosaico de modelos de Modelo.
 */
const MosaicoAcabados: React.FC<{ acabados: Acabado[] }> = ({ acabados }) => {
  if (acabados.length === 0) {
    return <Palette className="w-6 h-6 text-black/35" strokeWidth={1.5} />;
  }
  const alaVista = acabados.length > 4 ? acabados.slice(0, 3) : acabados.slice(0, 4);
  const resto = acabados.length - alaVista.length;
  return (
    <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-px bg-black/[0.06]">
      {alaVista.map((a) => (
        <Muestra key={a.id} acabado={a} className="w-full h-full" />
      ))}
      {resto > 0 && (
        <span className="grid place-items-center bg-white text-[10px] font-semibold text-black/55">
          +{resto}
        </span>
      )}
      {/* Con 1, 2 o 3 acabados y sin resto quedan celdas sin llenar: van en blanco, que es
          lo que hace que el mosaico siga leyéndose como una grilla de 2×2. */}
      {resto === 0 &&
        Array.from({ length: 4 - alaVista.length }).map((_, i) => (
          <span key={`vacia-${i}`} className="bg-white" />
        ))}
    </div>
  );
};

/**
 * El mismo mosaico, para las fotos de partida: la miniatura de la card cuando se eligió
 * más de una. Con una sola la card muestra la foto entera y no pasa por acá.
 *
 * Con DOS son dos mitades verticales, no dos celdas de una grilla de 2×2: la fila de
 * abajo quedaba en blanco y la miniatura leía "faltan fotos" en vez de "hay dos". Los
 * cuadrantes recién aparecen cuando hay algo que poner abajo, o sea de tres para arriba.
 */
const MosaicoImagenes: React.FC<{ imagenes: ImagenOrigen[] }> = ({ imagenes }) => {
  const dosMitades = imagenes.length === 2;
  const alaVista = imagenes.length > 4 ? imagenes.slice(0, 3) : imagenes.slice(0, 4);
  const resto = imagenes.length - alaVista.length;
  return (
    <div
      className={`absolute inset-0 grid grid-cols-2 gap-px bg-black/[0.06] ${
        dosMitades ? 'grid-rows-1' : 'grid-rows-2'
      }`}
    >
      {alaVista.map((o) => (
        <CachedImg key={o.key} src={o.url} alt="" className="w-full h-full object-cover" />
      ))}
      {resto > 0 && (
        <span className="grid place-items-center bg-white text-[10px] font-semibold text-black/55">
          +{resto}
        </span>
      )}
      {resto === 0 &&
        !dosMitades &&
        Array.from({ length: 4 - alaVista.length }).map((_, i) => (
          <span key={`vacia-${i}`} className="bg-white" />
        ))}
    </div>
  );
};

/**
 * Recuadro "generando" de una variante: el mismo de `PanelTandaCreaciones`, con el fondo
 * animado con el que la app ya dice "esto lo está haciendo la IA", y la pastilla del
 * acabado al pie —que es lo que distingue una celda de la de al lado—.
 *
 * Con más de una foto de partida el acabado ya no alcanza para distinguirlas —hay una
 * celda "roble" por foto—, así que en ese caso se dibuja arriba la miniatura de CUÁL.
 */
const RecuadroVariante: React.FC<{
  ratio: number;
  celda: CeldaVariante;
  origenUrl?: string;
}> = ({ ratio, celda, origenUrl }) => {
  const { acabado, estado } = celda;
  const pastilla = (
    <div className="absolute inset-x-2 bottom-2 z-10 flex items-center gap-1.5 rounded-full bg-white/95 px-2 py-1.5 backdrop-blur-sm">
      <Muestra
        acabado={acabado}
        className="w-3 h-3 rounded-full shrink-0 ring-1 ring-black/10"
      />
      <span className="text-[11px] font-semibold text-brand-black truncate">
        {acabado.nombre}
      </span>
    </div>
  );
  const miniaturaOrigen = origenUrl && (
    <span className="absolute top-2 left-2 z-10 w-8 h-8 rounded-lg overflow-hidden ring-2 ring-white/70 shadow-sm">
      <CachedImg src={origenUrl} alt="" className="w-full h-full object-cover" />
    </span>
  );

  if (estado === 'lista' && celda.url) {
    return (
      <div
        className="relative overflow-hidden rounded-2xl bg-black/[0.04]"
        style={{ aspectRatio: String(ratio) }}
      >
        <CachedImg
          src={celda.url}
          alt={acabado.nombre}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {miniaturaOrigen}
        {pastilla}
      </div>
    );
  }
  if (estado === 'error') {
    // La celda se queda en su lugar y dice por qué: sacarla de la grilla haría que la
    // tanda "pierda" un acabado sin explicación, y el texto ya aclara que no se cobró.
    return (
      <div
        className="relative overflow-hidden rounded-2xl bg-black/[0.05] ring-1 ring-black/[0.06]"
        style={{ aspectRatio: String(ratio) }}
      >
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1.5 px-3 pb-8 text-center">
          <AlertTriangle className="w-5 h-5 text-black/40" strokeWidth={1.8} />
          <span className="text-[11.5px] leading-snug text-black/55">{celda.mensaje}</span>
        </div>
        {miniaturaOrigen}
        {pastilla}
      </div>
    );
  }
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-brand-primary"
      style={{ aspectRatio: String(ratio) }}
    >
      <AnimatedBlurBg selected />
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2.5 px-3 text-white">
        <Sparkles className="w-7 h-7 drop-shadow" strokeWidth={1.5} />
        <span className="relative h-1 w-2/3 overflow-hidden rounded-full bg-white/30">
          <span className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-white animate-tanda-barra" />
        </span>
      </div>
      {miniaturaOrigen}
      {pastilla}
    </div>
  );
};

export default function VariantesAcabadoMode() {
  const navigate = useNavigate();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  // Qué pantalla se dibuja. Se decide en JS y no sólo con `md:` porque las dos ramas no
  // son la misma pantalla con otro CSS: montarlas a la vez levantaría DOS grillas de
  // creaciones —cada una con sus consultas y sus URLs firmadas— y las capas de mobile se
  // portalean al `body`, fuera del alcance de cualquier `md:hidden` que les pusiéramos
  // encima. Es el mismo corte de 768px con el que el shell elige sidebar o barra
  // inferior, así que la pantalla del modo y su chrome cambian juntos.
  const esEscritorio = useShellEscritorio();
  // La marca activa sale del MISMO hook que el gate de rubro, y no de leer
  // `current_brand_id` una vez al montar: así sigue al cambio de marca con la pantalla
  // abierta y, en las cuentas que nunca eligieron una, resuelve la `is_default` en vez de
  // `null` (ADR-2026-09-30-los-modos-de-hogar-siguen-a-la-marca-activa). Lo elegido NO se
  // vacía al cambiar: son imágenes, no productos de una marca.
  const { rubro, marcaId: brandId, loaded: rubroCargado } = useRubroMarcaActiva();

  // ── Gate de rubro ─────────────────────────────────────────────────────────
  // La card ya no se muestra fuera de Hogar y Decoración (`soloHogarDeco`), pero la ruta
  // existe igual: alguien con el link, o una cuenta que cambió de marca con la pantalla
  // abierta. Con el rubro resuelto y sin ser el correcto, a la home.
  useEffect(() => {
    if (rubroCargado && !esRubroHogarDeco(rubro)) {
      navigate('/interfaz-v2', { replace: true });
    }
  }, [rubroCargado, rubro, navigate]);

  // ── Cuenta ────────────────────────────────────────────────────────────────
  // La empresa es lo que necesita la grilla de creaciones. La marca activa y el catálogo
  // se cargan SÓLO para la cara de la card de arranque: lo que se elige en este modo
  // siguen siendo imágenes, no productos
  // (ADR-2026-09-03-variantes-parte-de-una-imagen-no-de-un-producto).
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [miniaturasCatalogo, setMiniaturasCatalogo] = useState<string[]>([]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      // Sólo la empresa: la marca la resuelve `useRubroMarcaActiva`, arriba.
      const { data: perfil } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (cancelado) return;
      setCompanyId((perfil as { id_company?: string | null } | null)?.id_company ?? null);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  /**
   * Las últimas fotos del catálogo, para la cara de la card de arranque.
   *
   * `fetchUserProducts` devuelve el catálogo ordenado por `created_at` descendente, así
   * que las primeras SON las últimas cargadas; se filtran las que no tienen imagen
   * firmada —un hueco blanco en el abanico se lee como una foto rota—. Es el mismo
   * criterio que la card vacía de "Ambiente completo"
   * (ADR-2026-09-10-la-card-vacia-muestra-los-ultimos-productos).
   *
   * Se carga acotado a la MARCA activa, que es lo que deja las miniaturas dentro del
   * rubro: en una empresa con marcas de varios rubros, el catálogo entero traía remeras a
   * la pantalla de arranque de un modo de muebles. Por eso espera a `rubroCargado`: con
   * `brandId` todavía en `null` traería el catálogo entero.
   */
  useEffect(() => {
    if (!companyId || !rubroCargado) return;
    let cancelado = false;
    (async () => {
      try {
        const lista = await fetchUserProducts(companyId, brandId);
        if (cancelado) return;
        setMiniaturasCatalogo(
          lista
            .map((p) => p.imageUrl)
            .filter((u): u is string => !!u)
            .slice(0, MINIATURAS_CARD),
        );
      } catch (e) {
        console.error('[VariantesAcabadoMode] catálogo para las miniaturas', e);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [companyId, brandId, rubroCargado]);

  // ── Lo que se elige ───────────────────────────────────────────────────────
  // Arranca en la presentación SÓLO en el teléfono: en desktop ese paso no existe (ver
  // `Paso`). `useShellEscritorio` resuelve sincrónicamente en el primer render, y cruzar
  // los 768px remonta el árbol entero, que es cuando este valor se vuelve a elegir.
  const [paso, setPaso] = useState<Paso>(esEscritorio ? 'producto' : 'presentacion');
  // El overlay de generación, SÓLO en el teléfono: en desktop la tanda aparece en la
  // columna de la derecha, que se ve entera mientras se generan.
  const [generandoMobile, setGenerandoMobile] = useState(false);
  const [origenes, setOrigenes] = useState<ImagenOrigen[]>([]);
  /**
   * Los acabados de CADA foto, por su `key`. Con una sola foto es un mapa de una entrada
   * y la pantalla se ve igual que antes; con dos o más, cada una lleva los suyos
   * (ADR-2026-09-04-cada-imagen-lleva-sus-acabados).
   */
  const [acabadosPorImagen, setAcabadosPorImagen] = useState<Record<string, string[]>>(
    {},
  );
  /** De qué foto son los acabados que está editando el panel. */
  const [imagenEnEdicion, setImagenEnEdicion] = useState<string | null>(null);
  /**
   * Si la configuración es UNA para todas las fotos o una por foto
   * (ADR-2026-09-04-un-switch-decide-si-la-paleta-es-una-o-una-por-foto).
   *
   * Arranca en `false` —una por una—: es el estado que MUESTRA lo que hay, una fila por
   * foto con lo que le tocó, y desde ahí "Todas por igual" se lee como un atajo que se
   * toma a propósito. Al revés, el default unificaba y escondía las fotos detrás de una
   * card sola antes de que se entendiera que existían.
   */
  const [configIgualEnTodas, setConfigIgualEnTodas] = useState(false);
  const [misMuestras, setMisMuestras] = useState<Acabado[]>([]);
  /**
   * El fondo de CADA foto, por su `key` — como los acabados
   * (ADR-2026-09-04-se-va-el-encuadre-y-el-fondo-va-por-imagen). Una foto sin entrada
   * vale `original`: heredar el fondo de la propia foto es lo que no sorprende.
   */
  const [fondosPorImagen, setFondosPorImagen] = useState<Record<string, FondoId>>({});
  /**
   * El fondo DESCRITO con palabras, por foto: "pared de microcemento", "piso de madera
   * clara y una planta". Las tres opciones dicen de qué TIPO es el fondo; esto dice cuál
   * (ADR-2026-09-04-el-fondo-se-explica-y-se-describe). Vacío = la opción sola.
   */
  const [detalleFondoPorImagen, setDetalleFondoPorImagen] = useState<
    Record<string, string>
  >({});
  const [calidad, setCalidad] = useState<CalidadImagen>(CALIDAD_DEFAULT);
  const [formatoId, setFormatoId] = useState<string>(DEFAULT_FORMAT_ID);
  const [instruccion, setInstruccion] = useState('');
  const [tandas, setTandas] = useState<TandaVariantes[]>([]);
  const [panelLateral, setPanelLateral] = useState<PanelLateral>(null);
  /** Subiendo y disparando: el botón se apaga hasta que el backend acepta la tanda. */
  const [generando, setGenerando] = useState(false);
  /** Lo que falló ANTES de que exista una tanda con celdas (o la tanda entera). */
  const [errorTanda, setErrorTanda] = useState<string | null>(null);
  /**
   * El plan de la empresa, como lo dejó el último guard de saldo.
   *
   * Un 402 del backend no dice de qué plan viene, y los dos modales son distintos: al de
   * plan gratis hay que ofrecerle suscribirse (`free-trial-ended`), no decirle que se
   * quedó sin lo de su plan. El guard de arriba ya lo distingue; el 402 llegaba siempre a
   * `plan-limit-reached` porque el `plan` moría dentro del `try`. En un ref y no en estado
   * porque lo leen un callback del hook y el `catch`, no el render — mismo arreglo que
   * Carrusel (ADR-2026-09-25-el-402-abre-el-modal-del-plan-que-tiene-la-empresa).
   */
  const planRef = useRef<string | null>(null);

  /**
   * La COLUMNA DE RESULTADOS de la derecha: el MISMO `PanelTandaCreaciones` que usan
   * Automática, Modelo, Carrusel y Animar, en vez de la grilla propia que tenía este modo
   * (ADR-2026-09-24-variantes-usa-la-columna-de-resultados-de-siempre).
   *
   * Lo que se gana no es sólo el look: las variantes quedan JUNTO al resto de lo generado
   * —agrupadas por fecha, con el historial de la empresa debajo—, se abren en el visor, se
   * descargan, y la tanda sobrevive a que uno se vaya del modo porque el estado vive en
   * `generacionesStore` y no en un `useState` de esta pantalla.
   *
   * Sólo en DESKTOP: en el teléfono no hay columna de la derecha y los resultados siguen
   * teniendo su pantalla propia (ADR-2026-09-10-variantes-mobile-genera-en-pantalla-completa),
   * que además muestra el estado acabado por acabado, que es lo que esta columna no modela.
   */
  const { columna, hayColumna, visorAbierto, lanzarTanda } = useColumnaResultadosModo({
    companyId,
    brandId,
    modo: 'variantes-acabado',
    onError: setErrorTanda,
    onLimitePlan: () =>
      setLimitModal(
        planRef.current === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
      ),
  });
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  /** Cierra el overlay del teléfono cuando la tanda terminó (`completeNow`). */
  const [completarMobile, setCompletarMobile] = useState(false);
  const estimadoMobileRef = useRef(cargaEstimadaMs(1));
  /**
   * El ARCHIVO de cada muestra subida, por id de acabado. `Acabado.imageUrl` es el
   * `objectURL` para dibujarla; lo que viaja al backend es el archivo, subido a storage
   * recién al generar (una muestra que se sube y se saca no deja nada en el bucket).
   */
  const archivosMuestra = useRef<Map<string, File>>(new Map());

  // Los `objectURL` de los muestras y de las imágenes subidas se liberan AL SALIR, y a
  // propósito no antes —a diferencia de "Mostrar cómo funciona", que los suelta al
  // reemplazarlos—: acá el resultado CONGELA lo que se usó. `TandaVariantes.porImagen`
  // se queda con el `ImagenOrigen` entero (la tarjeta de resultados dibuja `o.url`) y
  // con los `Acabado` elegidos (un muestra subido lleva su `imageUrl`). Soltar la url al
  // quitar una foto de la selección, o al sacar un muestra de la paleta, rompía las
  // tandas ya generadas que la seguían mostrando. Mientras el modo esté en pantalla, el
  // blob tiene que vivir.
  const urlsCreadas = useRef<string[]>([]);
  useEffect(
    () => () => {
      urlsCreadas.current.forEach((u) => URL.revokeObjectURL(u));
    },
    [],
  );

  /**
   * Todos los acabados disponibles: los curados más los que subió el usuario. Se arma
   * acá y no en el módulo de dominio porque `mis-muestras` es estado de la sesión.
   */
  const catalogoAcabados = useMemo(
    () => [...ACABADOS, ...misMuestras],
    [misMuestras],
  );

  /**
   * El mapa sigue a la selección de fotos: la que ENTRA arranca con lo mismo que la
   * primera —lo normal es querer la misma paleta en todas y después diferenciar una, no
   * armar cada una desde cero— y la que SALE se lleva su entrada, que si no queda
   * contando acabados de una foto que ya no está.
   */
  useEffect(() => {
    setAcabadosPorImagen((prev) => {
      const claves = origenes.map((o) => o.key);
      const semilla =
        claves.length > 0 ? (prev[claves[0]] ?? ACABADOS_POR_DEFECTO) : ACABADOS_POR_DEFECTO;
      let cambio = Object.keys(prev).length !== claves.length;
      const siguiente: Record<string, string[]> = {};
      for (const k of claves) {
        if (prev[k]) {
          siguiente[k] = prev[k];
        } else {
          siguiente[k] = semilla;
          cambio = true;
        }
      }
      return cambio ? siguiente : prev;
    });
    // El fondo va por el mismo camino: la foto que entra hereda el de la primera, la que
    // sale se lleva su entrada.
    setFondosPorImagen((prev) => {
      const claves = origenes.map((o) => o.key);
      const semilla = claves.length > 0 ? (prev[claves[0]] ?? 'original') : 'original';
      let cambio = Object.keys(prev).length !== claves.length;
      const siguiente: Record<string, FondoId> = {};
      for (const k of claves) {
        if (prev[k]) {
          siguiente[k] = prev[k];
        } else {
          siguiente[k] = semilla;
          cambio = true;
        }
      }
      return cambio ? siguiente : prev;
    });
  }, [origenes]);

  /** El fondo de una foto. Sin entrada, el de la propia foto. */
  const fondoDe = (key: string) => fondoPorId(fondosPorImagen[key] ?? 'original');

  /**
   * Cambia el fondo de la foto que el panel está editando — o de TODAS con el switch en
   * "todas por igual". El fondo viaja con el acabado y no aparte porque los dos se eligen
   * en el MISMO panel: una card única que aplicara la paleta a todas pero el fondo a una
   * sola sería mentira.
   */
  const elegirFondo = (id: FondoId) => {
    const key = imagenEnEdicion;
    if (!key) return;
    setFondosPorImagen((prev) => {
      if (!configIgualEnTodas) return { ...prev, [key]: id };
      const todas: Record<string, FondoId> = {};
      for (const o of origenes) todas[o.key] = id;
      return todas;
    });
  };

  /** El fondo escrito, con la misma regla: si la config va igual en todas, se copia. */
  const escribirFondo = (texto: string) => {
    const key = imagenEnEdicion;
    if (!key) return;
    setDetalleFondoPorImagen((prev) => {
      if (!configIgualEnTodas) return { ...prev, [key]: texto };
      const todas: Record<string, string> = {};
      for (const o of origenes) todas[o.key] = texto;
      return todas;
    });
  };

  /**
   * Los acabados de una foto, EN EL ORDEN DEL CATÁLOGO y no en el que se fueron tocando:
   * la card, el panel y la grilla de resultados tienen que decir lo mismo cada vez que se
   * miran.
   */
  const acabadosDe = useCallback(
    (key: string) => {
      const ids = acabadosPorImagen[key] ?? [];
      return catalogoAcabados.filter((a) => ids.includes(a.id));
    },
    [acabadosPorImagen, catalogoAcabados],
  );

  /**
   * Toca un acabado de la foto que el panel está editando — o de TODAS, si el switch está
   * en "todas por igual": ahí la card de la columna es una sola y tiene que gobernar todo
   * lo que dice.
   */
  const alternarAcabado = (id: string) => {
    const key = imagenEnEdicion;
    if (!key) return;
    setAcabadosPorImagen((prev) => {
      const actual = prev[key] ?? [];
      const siguiente = actual.includes(id)
        ? actual.filter((x) => x !== id)
        : [...actual, id];
      if (!configIgualEnTodas) return { ...prev, [key]: siguiente };
      const todas: Record<string, string[]> = {};
      for (const o of origenes) todas[o.key] = [...siguiente];
      return todas;
    });
  };

  /**
   * Lo elegido para la foto que se está editando, a TODAS. El atajo del caso normal:
   * separar la paleta por foto es lo excepcional, y sin esto elegir ocho acabados para
   * cuatro fotos eran cuatro vueltas iguales.
   */
  const aplicarAcabadosATodas = () => {
    const key = imagenEnEdicion;
    if (!key) return;
    setAcabadosPorImagen((prev) => {
      const lista = prev[key] ?? [];
      const siguiente: Record<string, string[]> = {};
      for (const o of origenes) siguiente[o.key] = [...lista];
      return siguiente;
    });
  };

  /**
   * Mueve el switch. Pasar a "todas por igual" UNIFICA de verdad —no alcanza con dibujar
   * una sola card, porque lo que se genera sale del mapa— y unifica con la config de la
   * primera foto que tenga algo elegido, no con la de la primera a secas: si alguien
   * configuró la Imagen 2 y dejó la 1 vacía, unificar en vacío le borra lo único que
   * había hecho, en silencio y sin poder deshacerlo.
   *
   * Pasar a "una por una" no toca nada: quedan todas iguales y desde ahí se diferencia la
   * que haga falta.
   */
  const ponerConfigIgual = (igual: boolean) => {
    setConfigIgualEnTodas(igual);
    if (!igual || origenes.length === 0) return;
    const patron =
      origenes.find((o) => (acabadosPorImagen[o.key] ?? []).length > 0) ?? origenes[0];
    const lista = acabadosPorImagen[patron.key] ?? [];
    const fondo = fondosPorImagen[patron.key] ?? 'original';
    setAcabadosPorImagen(
      Object.fromEntries(origenes.map((o) => [o.key, [...lista]])),
    );
    setFondosPorImagen(Object.fromEntries(origenes.map((o) => [o.key, fondo])));
  };

  const inputMuestraRef = useRef<HTMLInputElement>(null);
  const inputImagenRef = useRef<HTMLInputElement>(null);

  /**
   * Elegir las fotos de partida. Las dos vías dejan lo mismo en `origenes`.
   *
   * Tocar una creación ALTERNA en vez de reemplazar: la selección es múltiple, así que un
   * segundo toque tiene que ser cómo se saca. Y por lo mismo el panel ya no se cierra
   * solo — cerrarlo es el "Listo" del pie.
   *
   * El archivo se queda LOCAL (`objectURL`) en vez de subirse a un bucket como en
   * "Animar imagen": este modo todavía no genera, así que subir dejaría archivos
   * huérfanos en storage por cada prueba. Cuando haya backend, la subida real es
   * cambiar este handler por `subirImagenParaAnimar` — ver los pendientes del doc.
   */
  const elegirGenerada = (pick: GeneradaPick) =>
    setOrigenes((prev) =>
      prev.some((o) => o.key === pick.id)
        ? prev.filter((o) => o.key !== pick.id)
        : [
            ...prev,
            {
              key: pick.id,
              tipo: 'generada',
              id: pick.id,
              url: pick.signedUrl,
              nombre: 'Creación tuya',
            },
          ],
    );

  /**
   * El SET de un producto de una: todas las creaciones que la grilla tiene a la vista
   * (ADR-2026-09-04-elegir-el-set-de-un-producto).
   *
   * Agrega o saca, según lo que pida la grilla, y respeta lo que ya estaba elegido de
   * OTROS productos: el filtro acota lo que se ve, no lo que se lleva elegido.
   */
  const elegirVariasGeneradas = (picks: GeneradaPick[], elegir: boolean) =>
    setOrigenes((prev) => {
      const claves = new Set(picks.map((p) => p.id));
      if (!elegir) return prev.filter((o) => !claves.has(o.key));
      const yaEstan = new Set(prev.map((o) => o.key));
      const nuevos: ImagenOrigen[] = picks
        .filter((p) => !yaEstan.has(p.id))
        .map((p) => ({
          key: p.id,
          tipo: 'generada',
          id: p.id,
          url: p.signedUrl,
          nombre: p.producto ?? 'Creación tuya',
        }));
      return [...prev, ...nuevos];
    });

  const onArchivoImagen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivos = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (archivos.length === 0) return;
    const sello = Date.now();
    const nuevos: ImagenOrigen[] = archivos.map((archivo, i) => {
      const url = URL.createObjectURL(archivo);
      urlsCreadas.current.push(url);
      return {
        key: `subida-${sello}-${i}`,
        tipo: 'subida',
        url,
        archivo,
        nombre: archivo.name,
      };
    });
    setOrigenes((prev) => [...prev, ...nuevos]);
  };

  const quitarOrigen = (key: string) =>
    setOrigenes((prev) => prev.filter((o) => o.key !== key));

  /** Las subidas van aparte: la grilla de creaciones no las conoce. */
  const subidas = origenes.filter((o) => o.tipo === 'subida');

  /**
   * Las imágenes de partida con la forma que pide `CardProductos` (id/nombre/foto), para
   * la card de la pantalla de arranque. Es una traducción de tipos, no un dato nuevo: la
   * card se diseñó para productos y acá lo que se elige son fotos.
   */
  const origenesComoCards = useMemo(
    () =>
      origenes.map((o) => ({
        id: o.key,
        name: o.nombre,
        imageUrl: o.url,
      })),
    [origenes],
  );

  /**
   * El bloque de "de dónde salen las fotos": UNA sola grilla, con la card de subir
   * primera, después lo que el usuario subió y después sus creaciones
   * (ADR-2026-09-04-subir-imagen-es-la-primera-celda-de-la-grilla).
   *
   * Ofrece lo mismo que "Animar imagen" —subir o partir de algo que ya generaste— y se
   * dibuja igual en los dos lugares donde hace falta: la columna del paso 1 y el panel
   * lateral del paso 2.
   */
  const bloqueElegirImagen = (
    <GeneradasPickGrid
      companyId={companyId}
      selectedId={null}
      selectedIds={origenes.filter((o) => o.tipo === 'generada').map((o) => o.key)}
      onSelect={elegirGenerada}
      emptyHint="Todavía no generaste ninguna imagen. Subí las tuyas desde tu computadora."
      conFiltroProducto
      rubro={rubro}
      onElegirVarias={elegirVariasGeneradas}
      tileInicial={
        <>
          {/* La card de subir: la misma celda cuadrada que las fotos, en punteado y en
              azul —la forma que ya tiene "Subir muestra" en el panel de acabados—. */}
          <button
            type="button"
            onClick={() => inputImagenRef.current?.click()}
            className="aspect-square rounded-[13px] bg-brand-primary/[0.04] border-[1.5px] border-dashed border-brand-primary/35 hover:border-brand-primary/60 hover:bg-brand-primary/[0.07] grid place-items-center transition-colors"
          >
            <span className="flex flex-col items-center gap-1.5 px-2">
              <span className="w-9 h-9 rounded-full bg-brand-primary/10 grid place-items-center text-brand-primary">
                <ImageUp className="w-[18px] h-[18px]" />
              </span>
              <span className="text-[12.5px] font-semibold text-brand-primary text-center leading-tight">
                Subir imagen
              </span>
            </span>
          </button>
          {/* Lo subido, en la misma celda que las creaciones para que la selección se lea
              como una sola: la única diferencia es que se saca con la cruz y no
              volviéndola a tocar, porque acá no hay dónde volver a encontrarla. */}
          {subidas.map((o) => (
            <div
              key={o.key}
              className="relative aspect-square rounded-[13px] overflow-hidden bg-neutral-100 ring-2 ring-brand-primary"
            >
              <img src={o.url} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => quitarOrigen(o.key)}
                aria-label={`Quitar ${o.nombre}`}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/95 text-brand-black grid place-items-center shadow-sm hover:bg-white transition-colors"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2.6} />
              </button>
            </div>
          ))}
        </>
      }
    />
  );

  const onArchivoMuestra = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo) return;
    const url = URL.createObjectURL(archivo);
    urlsCreadas.current.push(url);
    const nuevo: Acabado = {
      id: `muestra-${Date.now()}`,
      // El nombre del archivo sin extensión: es lo único que se sabe del muestra, y
      // llamarlo "Muestra 1" obligaría a renombrarlo para distinguirlo del siguiente.
      nombre: archivo.name.replace(/\.[^.]+$/, '').slice(0, 28) || 'Mi muestra',
      categoria: 'mis-muestras',
      color: '#FFFFFF',
      imageUrl: url,
    };
    archivosMuestra.current.set(nuevo.id, archivo);
    setMisMuestras((prev) => [...prev, nuevo]);
    // Subirlo ES elegirlo: nadie sube una tela para dejarla afuera de la tanda. Queda
    // elegido en la foto que el panel está editando, que es desde donde se subió.
    if (imagenEnEdicion) {
      setAcabadosPorImagen((prev) => ({
        ...prev,
        [imagenEnEdicion]: [...(prev[imagenEnEdicion] ?? []), nuevo.id],
      }));
    }
  };

  // ── Panel lateral: dónde arranca ──────────────────────────────────────────
  // Se mide el borde derecho de la columna de configuración, como en Modelo y Catálogo
  // (ADR-2026-09-02-el-panel-lateral-arranca-donde-termina-la-configuracion): el ancho es
  // elástico y escribirlo acá lo dejaría tapando la columna en cuanto cambie.
  const areaRef = useRef<HTMLDivElement>(null);
  const panelConfigRef = useRef<HTMLElement>(null);
  const [offsetPanelLateral, setOffsetPanelLateral] = useState<number | string>(
    `calc(${ANCHO_PANEL_CONFIG} + ${SEPARACION_PANEL}px)`,
  );
  useLayoutEffect(() => {
    const aside = panelConfigRef.current;
    const area = areaRef.current;
    if (!aside || !area) return;
    const medir = () => {
      const a = aside.getBoundingClientRect();
      const r = area.getBoundingClientRect();
      setOffsetPanelLateral(Math.round(a.right - r.left) + SEPARACION_PANEL);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(aside);
    ro.observe(area);
    return () => ro.disconnect();
    // `paso` en las dependencias: cada paso dibuja su propio `<aside>`, y el observer
    // tiene que engancharse al que está montado.
  }, [paso]);

  const alternarPanel = (p: Exclude<PanelLateral, null>) =>
    setPanelLateral((actual) => (actual === p ? null : p));

  /**
   * Abre el panel de acabados apuntando a UNA foto. Volver a tocar la misma fila lo
   * cierra; tocar la de otra foto cambia de foto sin cerrarlo, que es el gesto de ir
   * comparando paletas.
   */
  const abrirAcabadosDe = (key: string) => {
    setImagenEnEdicion(key);
    setPanelLateral((actual) =>
      actual === 'acabados' && imagenEnEdicion === key ? null : 'acabados',
    );
  };

  // ── Mobile: el back del chrome ────────────────────────────────────────────
  // El paso 2 NO dibuja su propio "Volver": el chrome flotante del shell ya pone uno
  // arriba a la izquierda, y las dos flechas harían el mismo movimiento una encima de la
  // otra (ADR-2026-09-04-modelo-mobile-un-solo-back-en-el-paso-2). Así que el back de
  // arriba retrocede DE PASO, y sólo desde el primero se va del modo. Además, ponerlo
  // esconde la barra inferior de navegación, que es lo que hacen el resto de los modos al
  // ocupar la pantalla entera.
  useEffect(() => {
    setMobileBackOverride(() => () => {
      // Desde los acabados el back vuelve a la PRESENTACIÓN y no a la grilla: la grilla
      // es un paso de ida —se entra, se elige y se sale con "Siguiente"—, y volver ahí
      // devolvía a una pantalla de selección que ya estaba resuelta. Para cambiar las
      // fotos está la card de "Imágenes de partida", que abre esa misma grilla a
      // propósito. Los resultados sí vuelven a los acabados: es de donde salieron, y es
      // lo mismo que hace su botón "Seguir generando".
      if (paso === 'resultados') setPaso('configurar');
      else if (paso === 'configurar' || paso === 'producto') setPaso('presentacion');
      else navigate('/interfaz-v2');
    });
    return () => setMobileBackOverride(null);
  }, [paso, setMobileBackOverride, navigate]);

  // ── La tanda ──────────────────────────────────────────────────────────────
  // La cuenta del modo es FOTO × SUS ACABADOS: cada foto sale una vez por cada acabado
  // que le tocó, y ya no son los mismos para todas
  // (ADR-2026-09-04-cada-imagen-lleva-sus-acabados).
  const cantidad = origenes.reduce((n, o) => n + acabadosDe(o.key).length, 0);
  const costo = costoTanda(cantidad, calidad);

  /** La primera elegida: es la que representa a la selección en las miniaturas. */
  const primerOrigen = origenes[0] ?? null;

  /** Los acabados de la primera, que con UNA sola foto son los de la tanda entera. */
  const acabadosDelPrimero = primerOrigen ? acabadosDe(primerOrigen.key) : [];

  /**
   * La bajada de la fila de una foto: cuántos acabados y con qué fondo. Reemplaza a
   * `resumenAcabados`, que decía las categorías: con el fondo adentro del mismo panel,
   * lo que hay que poder leer sin abrirlo son las dos decisiones, no de qué familia son
   * las muestras.
   */
  const resumenDeImagen = (key: string) => {
    const n = acabadosDe(key).length;
    const escrito = (detalleFondoPorImagen[key] ?? '').trim();
    const fondo =
      fondoDe(key).id === 'describir' && escrito ? escrito : fondoDe(key).valor;
    return `${n === 0 ? 'Sin acabados' : `${n} ${n === 1 ? 'acabado' : 'acabados'}`} · ${fondo}`;
  };

  /**
   * Todas las fotos con la MISMA paleta. Sirve para el pie: mientras no se diferencie
   * ninguna, la cuenta se puede decir como la multiplicación de siempre.
   */
  const mismosAcabadosEnTodas = useMemo(() => {
    if (origenes.length === 0) return true;
    const firma = (k: string) => [...(acabadosPorImagen[k] ?? [])].sort().join(',');
    const primera = firma(origenes[0].key);
    return origenes.every((o) => firma(o.key) === primera);
  }, [origenes, acabadosPorImagen]);

  // El orden importa: primero lo que falta elegir y recién después lo que falta escribir.
  const sinAcabados = origenes.some((o) => acabadosDe(o.key).length === 0);
  const sinEscribirElFondo = origenes.some(
    (o) =>
      fondoDe(o.key).id === 'describir' && !(detalleFondoPorImagen[o.key] ?? '').trim(),
  );
  const falta: string | null =
    origenes.length === 0
      ? 'Elegí al menos una imagen.'
      : sinAcabados
        ? origenes.length === 1
          ? 'Elegí al menos un acabado.'
          : 'Hay imágenes sin acabados elegidos.'
        : sinEscribirElFondo
          ? 'Escribí cómo querés el fondo.'
          : null;

  /**
   * Generar: la tanda se dibuja YA, con una celda por par, y se resuelve sola cuando el
   * job termina. La promesa no se espera desde el botón —en desktop el usuario se queda
   * en el modo y puede armar la siguiente—; en el teléfono la tapa el overlay hasta que
   * llega la respuesta (`completeNow`).
   *
   * Orden: guard de saldo fresco (como Carrusel) → subir a storage lo que vino como
   * archivo (fotos y muestras, una sola vez cada una) → `POST` → 202 → poll → celdas.
   */
  const generar = async () => {
    if (falta || !companyId || generando) return;
    setErrorTanda(null);
    setGenerando(true);
    const porImagen = origenes.map((o) => ({ origen: o, acabados: acabadosDe(o.key) }));
    const celdas: CeldaVariante[] = porImagen.flatMap(({ origen: o, acabados }, imagenIndex) =>
      acabados.map((a) => ({
        key: `${o.key}-${a.id}`,
        imagenIndex,
        origen: o,
        acabado: a,
        estado: 'generando' as const,
      })),
    );
    const idTanda = `${Date.now()}`;
    const tanda: TandaVariantes = {
      id: idTanda,
      ratio: ratioDeFormato(formatoId),
      porImagen,
      celdas,
      creadaEn: Date.now(),
    };
    const actualizar = (fn: (t: TandaVariantes) => TandaVariantes) =>
      setTandas((ts) => ts.map((t) => (t.id === idTanda ? fn(t) : t)));
    const quitar = () => setTandas((ts) => ts.filter((t) => t.id !== idTanda));
    /**
     * Cierra en error las celdas que quedaron generando. El texto de la CELDA es
     * parámetro: si lo que pasó es que se agotó la espera de acá, el job sigue vivo y
     * decir "no se cobró" es mentirle al usuario —justo lo contrario de lo que dice el
     * mensaje de la tanda, que lo manda a Creaciones.
     */
    const fallarTodas = (mensaje: string, textoCelda = 'No salió · no se cobró') =>
      actualizar((t) => ({
        ...t,
        error: mensaje,
        celdas: t.celdas.map((c) =>
          c.estado === 'generando' ? { ...c, estado: 'error', mensaje: textoCelda } : c,
        ),
      }));

    /**
     * El puente con el ciclo de generaciones de la app: quiere UNA promesa con los ids
     * que salieron, y este modo los conoce recién al final del poll. El diferido deja que
     * los recuadros aparezcan YA —como en Automática— sin dar vuelta el flujo de abajo,
     * que es el que además alimenta la pantalla de resultados del teléfono.
     */
    let resolverIds: (ids: string[]) => void = () => {};
    let rechazarIds: (e: unknown) => void = () => {};
    const idsDeLaTanda = new Promise<string[]>((res, rej) => {
      resolverIds = res;
      rechazarIds = rej;
    });
    // `catch` vacío: quien reporta el error es `lanzarTanda`. Sin esto, si el tope de
    // tandas en curso estuviera lleno y nadie quedara escuchando, rechazarla tiraría un
    // unhandled rejection en la consola.
    idsDeLaTanda.catch(() => {});

    /**
     * El `execution_id`, apenas el backend acepta. Queda anotado en la tanda del store:
     * sin eso, recargar pierde el seguimiento de una corrida que el backend ya aceptó y
     * que va a cobrar igual.
     */
    let resolverExecution: (id: string | null) => void = () => {};
    const executionDeLaTanda = new Promise<string | null>((res) => {
      resolverExecution = res;
    });

    /**
     * La tanda va al store SIEMPRE, también en el teléfono
     * (ADR-2026-09-25-revision-greptile-en-el-telefono-la-tanda-tambien-se-anota).
     *
     * En desktop además se ve: el store es lo que dibuja la columna. En el teléfono no
     * hay columna —los resultados tienen su pantalla propia, alimentada por `tandas`— y
     * durante un tiempo eso hizo creer que el store no hacía falta ahí. Sí hace falta:
     * es lo único que anota el `execution_id`, y sin él recargar el teléfono a mitad de
     * una generación perdía el rastro de algo que el backend iba a cobrar igual. De
     * paso, la generación aparece en el aviso de "generando de fondo" y, si la persona
     * abre la app en la compu, en su columna.
     *
     * Devuelve `false` cuando el tope de tandas en curso está lleno: ahí avisa y NO crea
     * tanda, y su manejador del rechazo es un no-op. O sea que en ese caso nadie reporta
     * lo que salga mal y tiene que hacerlo el modo
     * (ADR-2026-09-25-revision-greptile-sin-tanda-el-modo-reporta-sus-errores).
     */
    const enElStore = lanzarTanda({
      total: celdas.length,
      // Lo que la tanda RESERVA, que no es una por imagen: en 2K y 4K el backend
      // multiplica (×2 en 4K) y `costoTanda` ya lo tiene en cuenta. Sin esto, el store
      // reservaba de menos y los guards de las otras generaciones dejaban pasar un
      // pedido que parecía alcanzable y el backend después rechazaba
      // (ADR-2026-09-25-revision-greptile-la-tanda-reserva-lo-que-de-verdad-cuesta).
      creditos: costo,
      ratio: tanda.ratio,
      // El rótulo de los recuadros mientras generan. Con UN acabado es ése —que es lo
      // que distingue una variante de la de al lado—; con varios no hay uno que sirva
      // para todos, y la fecha sola alcanza hasta que las cards salgan con el suyo
      // (que lo pone el listado, desde `datos_generacion.acabado_id`).
      producto: celdas.length === 1 ? celdas[0].acabado.nombre : undefined,
      resultado: idsDeLaTanda,
      executionId: executionDeLaTanda,
      errorGenerico: 'No se pudo generar la tanda. Intentá nuevamente.',
    });
    // La tanda LOCAL es sólo del teléfono: es la que tiene el estado celda por celda que
    // dibuja su pantalla de resultados, que el store no modela.
    if (!esEscritorio) {
      setTandas((t) => [tanda, ...t]);
      estimadoMobileRef.current = cargaEstimadaMs(celdas.length);
      setCompletarMobile(false);
      setGenerandoMobile(true);
    }
    // En el teléfono el overlay termina en la pantalla de resultados; si la tanda no
    // llegó a existir (sin saldo), se cierra sin ir a ningún lado.
    let mostrarResultadosMobile = true;

    try {
      const { data: perfil } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes:imagenes_disponibles')
        .eq('id_company', companyId)
        .single();
      const restantes = (perfil as { imagenes_restantes?: number | null } | null)
        ?.imagenes_restantes;
      const plan = (perfil as { plan?: string } | null)?.plan;
      planRef.current = plan ?? null;
      if (typeof restantes === 'number' && restantes < costo) {
        mostrarResultadosMobile = false;
        // La tanda YA está en el store —se crea al tocar Generar, antes de este guard— y
        // sólo se cierra o se tira cuando la promesa se resuelve o se rechaza. Sin
        // rechazarla, los recuadros se quedaban en "Generando…" hasta recargar
        // (ADR-2026-09-25-revision-greptile-el-guard-de-saldo-cierra-la-tanda).
        rechazarIds(
          new GenerateAdError('billing', 'No alcanzan los créditos para esta tanda.'),
        );
        // El modal se abre ACÁ y no se delega al rechazo: con el tope de tandas lleno no
        // hay tanda, y ese rechazo se descarta —quien se quedó sin créditos veía sólo el
        // aviso del tope y ningún modal—. Llamarlo igual no lo abre dos veces: es el
        // mismo valor de estado que pondría `onLimitePlan`.
        setLimitModal(plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
        // La local es sólo del teléfono; en desktop no hay ninguna que sacar.
        quitar();
        resolverExecution(null);
        return;
      }

      const refsFoto = new Map<string, ReferenciaSubida>();
      for (const o of origenes) {
        if (o.tipo !== 'subida' || !o.archivo) continue;
        const subida = await subirImagenParaAnimar(o.archivo, companyId, 'variantes');
        refsFoto.set(o.key, { bucket: subida.bucket, path: subida.path });
      }
      const refsMuestra = new Map<string, ReferenciaSubida>();
      for (const { acabados } of porImagen) {
        for (const a of acabados) {
          if (a.categoria !== 'mis-muestras' || refsMuestra.has(a.id)) continue;
          const archivo = archivosMuestra.current.get(a.id);
          if (!archivo) continue;
          const subida = await subirImagenParaAnimar(archivo, companyId, 'variantes');
          refsMuestra.set(a.id, { bucket: subida.bucket, path: subida.path });
        }
      }

      const body = armarPedidoVariantes({
        fotos: porImagen.map(({ origen: o, acabados }) => ({
          origen:
            o.tipo === 'generada'
              ? { tipo: 'generada', id: o.id ?? o.key }
              : { tipo: 'subida', ref: refsFoto.get(o.key)! },
          acabados: acabados.map((a) => ({
            id: a.id,
            nombre: a.nombre,
            categoria: a.categoria,
            color: a.color,
            swatch: refsMuestra.get(a.id),
          })),
          fondo: fondoDe(o.key).id,
          fondoDescripcion: detalleFondoPorImagen[o.key] ?? null,
        })),
        formato: formatoDeRatioId(formatoId),
        calidad,
        instruccion,
      });

      const lanzada = await postVariantesAcabado(body);
      actualizar((t) => ({ ...t, executionId: lanzada.executionId }));
      resolverExecution(lanzada.executionId);
      // Ya está en vuelo: el botón vuelve, el usuario puede armar la próxima.
      setGenerando(false);

      const fin = await esperarVariantes(lanzada.executionId, celdas.length);
      if (!fin.resultados) {
        const mensaje = fin.mensaje ?? 'No se pudo generar la tanda. Intentá nuevamente.';
        if (fin.sigueEnCurso) {
          // Se agotó la espera de ACÁ, no la del backend: el job sigue vivo y va a
          // cobrar. NO se rechaza la promesa: rechazarla hace que la columna TIRE la
          // tanda del store (`fallarTanda`), y con ella el `execution_id`, que es lo único que la
          // vuelve recuperable después de recargar. Se queda pendiente —que es la
          // verdad— y el reanudador del shell la retoma al volver. Lo que sí se hace es
          // decirlo, abajo del botón.
          setErrorTanda(mensaje);
        } else {
          rechazarIds(new Error(mensaje));
        }
        // En el teléfono las celdas son las que cuentan el estado: si el job sigue, no
        // pueden decir que no salió ni que no se cobró.
        fallarTodas(
          mensaje,
          fin.sigueEnCurso ? 'Sigue generando · mirá en Creaciones' : undefined,
        );
        return;
      }
      const porPar = new Map(fin.resultados.map((r) => [`${r.imagen_index}:${r.acabado_id}`, r]));
      const ids = fin.resultados
        .map((r) => r.imagen_generada_id)
        .filter((id): id is string => typeof id === 'string');
      if (esEscritorio) {
        // Cierra la tanda del store: es lo que la saca de "generando" y dibuja las cards.
        resolverIds(ids);
        // Lo que SALIÓ lo dibuja la columna compartida; lo que no, no tiene celda allá,
        // así que se cuenta abajo del botón, con su acabado y su motivo. En el teléfono
        // no hace falta: ahí cada celda muestra el suyo.
        const fallados = celdas.flatMap((c) => {
          const r = porPar.get(`${c.imagenIndex}:${c.acabado.id}`);
          return r?.imagen_generada_id
            ? []
            : [{ acabado: c.acabado.nombre, motivo: textoDeErrorDePar(r?.error) }];
        });
        if (fallados.length > 0) {
          setErrorTanda(resumenDeFallados(fallados, celdas.length));
        }
        return;
      }
      /**
       * En el teléfono se precarga ACÁ y se resuelve la promesa DESPUÉS: así la precarga
       * del hook encuentra las firmas en caché y las imágenes ya decodificadas, en vez de
       * repetir los pedidos
       * (ADR-2026-09-25-revision-greptile-en-el-telefono-se-precarga-antes-de-resolver).
       *
       * Una sola espera ACOTADA, y todo lo que depende de la precarga cuelga de ella: el
       * cierre de la tanda (abajo) y el overlay de "Generando…" (el `finally`). Tener dos
       * caminos distintos fue exactamente el error de la vuelta anterior — se acotó el
       * cierre y el overlay se quedó esperando igual.
       */
      const precarga: Promise<PreloadedImage[]> =
        ids.length > 0 ? preloadGeneratedImages(ids, companyId) : Promise.resolve([]);
      /**
       * Pinta las celdas con las URLs que haya. Es una función y no un bloque porque se
       * llama DOS veces: con lo que llegó dentro del tope, y otra vez si la precarga
       * termina después — ver abajo.
       *
       * Una celda cuyo par salió pero todavía no tiene URL se deja GENERANDO, no en
       * error: "no se pudo cargar" es un veredicto, y acá lo único que pasó es que
       * todavía no llegó. Sólo se marcan en error las que el backend no entregó.
       */
      const pintarCeldas = (listas: PreloadedImage[], ultimaPasada: boolean) => {
        const urlPorId = new Map(listas.map((p) => [String(p.id), p.signedUrl]));
        actualizar((t) => ({
          ...t,
          celdas: t.celdas.map((c) => {
            const r = porPar.get(`${c.imagenIndex}:${c.acabado.id}`);
            if (r?.imagen_generada_id) {
              const url = urlPorId.get(r.imagen_generada_id);
              if (url) return { ...c, estado: 'lista' as const, url, imagenId: r.imagen_generada_id };
              return ultimaPasada
                ? {
                    ...c,
                    estado: 'error' as const,
                    mensaje: 'Salió, pero no se pudo cargar. Está en Creaciones.',
                  }
                : c;
            }
            return { ...c, estado: 'error' as const, mensaje: textoDeErrorDePar(r?.error) };
          }),
        }));
      };

      const precargadas = await conTope(precarga, ESPERA_MAX_PRECARGA_MS, []);
      resolverIds(ids);
      // Si ganó el tope, lo que volvió es vacío: se pinta lo que hay —nada— y las celdas
      // de los pares que SÍ salieron se quedan generando. Cuando la precarga termine de
      // verdad se vuelven a pintar, ahora sí como última pasada. Así una sola imagen
      // trabada no esconde las demás, que es lo que pasaba cuando el tope decidía por
      // todas (ADR-2026-09-25-revision-greptile-una-imagen-trabada-no-tapa-a-las-otras).
      const ganoElTope = precargadas.length === 0 && ids.length > 0;
      pintarCeldas(precargadas, !ganoElTope);
      if (ganoElTope) {
        precarga.then(
          (tarde) => pintarCeldas(tarde, true),
          () => pintarCeldas([], true),
        );
      }
    } catch (e) {
      console.error('[VariantesAcabadoMode] generar falló', e);
      // En desktop el error lo cuenta la columna compartida, que ya distingue el tope de
      // plan del resto y saca sus recuadros. Hacerlo también acá lo diría dos veces.
      // Sin 202 no hay nada que anotar; sin esto la promesa del id queda colgada.
      resolverExecution(null);
      // La tanda del store se tira en las dos plataformas: el hook ya distingue el tope
      // de plan del resto y saca sus recuadros.
      rechazarIds(e);
      // Con tanda en el store, el mensaje lo cuenta el hook: repetirlo acá lo diría dos
      // veces. Sin tanda —el tope estaba lleno— el rechazo se descarta y no lo cuenta
      // nadie, así que sigue de largo al camino de abajo, que sí reporta.
      if (esEscritorio && enElStore) {
        mostrarResultadosMobile = false;
        return;
      }
      if (e instanceof GenerateAdError && e.errorType === 'billing') {
        quitar();
        mostrarResultadosMobile = false;
        setLimitModal(
          planRef.current === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
        );
      } else {
        const mensaje =
          e instanceof GenerateAdError
            ? e.message
            : (e instanceof Error && e.message) ||
              'No se pudo generar la tanda. Intentá nuevamente.';
        fallarTodas(mensaje);
        setErrorTanda(mensaje);
      }
    } finally {
      setGenerando(false);
      if (!esEscritorio) {
        if (mostrarResultadosMobile) setCompletarMobile(true);
        else setGenerandoMobile(false);
      }
    }
  };

  // ── Migas ─────────────────────────────────────────────────────────────────
  // Igual que el Catálogo: el modo en el medio, el paso al final. Volver de "Configurar"
  // al producto es tocar el nombre del modo.
  const nombreModo = nombreCortoModo(PATH_MODO, 'Variantes de acabado');
  const migas =
    paso === 'configurar'
      ? [
          { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
          { label: nombreModo, onClick: () => setPaso('producto') },
          { label: 'Elegir acabados' },
        ]
      : [
          { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
          { label: nombreModo },
        ];

  // ── Piezas de la columna ──────────────────────────────────────────────────

  /**
   * Los acabados. Con UNA foto es la card de siempre —la paleta es la de la tanda—; con
   * dos o más, una fila POR FOTO, cada una con los suyos
   * (ADR-2026-09-04-cada-imagen-lleva-sus-acabados).
   *
   * Filas en la columna y no un selector de foto adentro del panel: quién lleva qué es lo
   * que hay que poder revisar antes de gastar los créditos, y adentro del panel había que
   * entrar y recorrer las fotos de a una para verlo. La miniatura de cada fila es la FOTO
   * —es lo que las distingue— y el mosaico de muestras queda para la card única.
   */
  /**
   * La foto de partida, con la misma forma de fila que el resto de las cards de la
   * columna. Reemplaza a `CardProductos`, que mostraba el producto del catálogo.
   *
   * En DESKTOP abre el panel lateral con la grilla; en MOBILE devuelve al paso 1, que es
   * donde esa grilla vive a pantalla completa — el mismo camino que "Editar la escena" en
   * "Ambiente completo". Tocarla no puede abrir una capa que muestre lo mismo que el paso
   * anterior.
   */
  const cardOrigen = (
    <CardFila
      etiqueta={origenes.length > 1 ? 'Imágenes de partida' : 'Imagen de partida'}
      // El segundo renglón no describe la foto elegida: invita a cambiarla. La miniatura
      // ya dice CUÁL es, así que repetirlo en gris ("Una creación tuya") gastaba el único
      // renglón que hay para decir que la card se toca.
      valor={origenes.length === 0 ? 'Sin elegir' : 'Cambiar imágenes'}
      valorEnPrimario={origenes.length > 0}
      activa={panelLateral === 'origen'}
      onClick={() => (esEscritorio ? alternarPanel('origen') : setPaso('producto'))}
      miniatura={
        origenes.length === 0 ? (
          <ImageUp className="w-6 h-6 text-black/35" strokeWidth={1.6} />
        ) : origenes.length === 1 ? (
          <CachedImg
            src={primerOrigen!.url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <MosaicoImagenes imagenes={origenes} />
        )
      }
    />
  );

  /** La card ÚNICA: con una sola foto, y con varias cuando el switch dice "por igual". */
  const cardAcabadosUnica = (
    <CardFila
      etiqueta="Acabados y fondo"
      valor={primerOrigen ? resumenDeImagen(primerOrigen.key) : 'Sin acabados'}
      activa={panelLateral === 'acabados'}
      onClick={() => primerOrigen && abrirAcabadosDe(primerOrigen.key)}
      miniatura={<MosaicoAcabados acabados={acabadosDelPrimero} />}
    />
  );

  const bloqueAcabados =
    origenes.length <= 1 ? (
      cardAcabadosUnica
    ) : (
      <div className="shrink-0 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3 px-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-black/45 leading-none">
            Acabados y fondo
          </p>
          <SwitchConfigPorImagen igual={configIgualEnTodas} onChange={ponerConfigIgual} />
        </div>
        {/* La bajada va ARRIBA de las cards y en los DOS modos: el switch es la única
            decisión de la pantalla que cambia la forma de lo que está abajo, y con dos
            etiquetas de tres palabras no alcanza para decir qué hace cada una. Antes sólo
            aparecía en "por igual", así que el modo elegido no se explicaba nunca. */}
        <p className="px-1 -mt-0.5 mb-0.5 text-[12.5px] leading-snug text-black/45">
          {configIgualEnTodas
            ? `Elegís una sola vez y se aplica a las ${origenes.length} imágenes.`
            : `Cada imagen lleva sus propios acabados y su fondo. Son ${origenes.length} configuraciones.`}
        </p>
        {configIgualEnTodas ? (
          cardAcabadosUnica
        ) : (
          <>
            {origenes.map((o, i) => (
              <CardFila
                key={o.key}
                etiqueta={`Imagen ${i + 1}`}
                valor={resumenDeImagen(o.key)}
                activa={panelLateral === 'acabados' && imagenEnEdicion === o.key}
                onClick={() => abrirAcabadosDe(o.key)}
                miniatura={
                  <CachedImg
                    src={o.url}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                }
              />
            ))}
          </>
        )}
      </div>
    );


  /**
   * La instrucción libre, ÚLTIMA — el mismo lugar y el mismo argumento que en "Mostrar
   * cómo funciona" (ADR-2026-09-03-el-paso-2-cierra-con-la-instruccion): primero se
   * decide, y lo último es afinar con palabras.
   *
   * ALTO FIJO, no `flex-1`: estirándose a lo que sobrara, el campo medía distinto según
   * cuánto ocupara el resto de la columna y en pantallas altas quedaba un rectángulo
   * enorme para dos renglones de texto.
   */
  const bloqueInstruccion = (
    <div className="relative shrink-0 h-[132px] flex flex-col">
      <textarea
        value={instruccion}
        onChange={(e) => setInstruccion(e.target.value)}
        placeholder="Agregá indicaciones extras para afinar el resultado"
        className="flex-1 w-full rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black placeholder:text-black/35 resize-none transition-colors"
      />
      {/* El mismo historial de instrucciones que el resto de los modos
          (ADR-2026-08-12-historial-instrucciones-reusables). El `companyId` va explícito:
          esta pantalla vive afuera del `AgentEnvContext` de `InterfazV2`. */}
      <PromptRecientesButton
        tipo="instruccion"
        companyId={companyId}
        onPick={setInstruccion}
        titulo="Instrucciones usadas antes"
        className="absolute bottom-2.5 right-2.5"
      />
    </div>
  );

  const pastillaCreditos = (
    <span className="inline-flex items-center gap-1 bg-white text-brand-primary px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
      <img
        src="/images/token_icon.png"
        alt=""
        aria-hidden="true"
        className="w-3.5 h-3.5 object-contain"
      />
      {costo}
    </span>
  );

  // El pie fijo. En el paso 2 van Calidad y Formato —las dos celdas del diseño— y el
  // Generar, que DICE la cantidad: no hay contador de imágenes porque la cuenta no se
  // elige, sale de los acabados.
  const pie =
    paso === 'producto' ? (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setPaso('configurar')}
          disabled={origenes.length === 0}
          className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none hover:opacity-90 transition-opacity"
        >
          Siguiente
        </button>
        {/* El pie dice CUÁNTAS van: con selección múltiple, el botón solo ya no lo dice. */}
        <p className="text-[13px] text-black/50 text-center leading-snug">
          {origenes.length === 0
            ? 'Elegí las fotos que se van a repintar en cada acabado.'
            : `${origenes.length} ${
                origenes.length === 1 ? 'imagen elegida' : 'imágenes elegidas'
              } · cada una sale una vez por acabado`}
        </p>
      </div>
    ) : (
      <>
        <div className="grid grid-cols-2 gap-2">
          <CeldaAjuste etiqueta="Calidad">
            <CalidadSegmentada calidad={calidad} onChange={setCalidad} />
          </CeldaAjuste>
          <CeldaAjuste
            etiqueta="Formato"
            onClick={() => alternarPanel('formato')}
            abrePanelLateral
          >
            <ValorConChevron texto={getFormatLabel(formatoId)} />
          </CeldaAjuste>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={generar}
            disabled={!!falta || generando}
            className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none hover:opacity-90 transition-opacity"
          >
            {/* Sin acabados elegidos el botón dice "Generar" pelado y sin la pastilla:
                "Generar 0 imágenes · 0" es el estado de arranque desde que la paleta viene
                vacía (ADR-2026-09-04-la-paleta-arranca-vacia), y no se anuncia una cuenta
                que todavía no existe. */}
            {cantidad === 0
              ? 'Generar'
              : cantidad === 1
                ? 'Generar 1 imagen'
                : `Generar ${cantidad} imágenes`}
            {cantidad > 0 && pastillaCreditos}
          </button>
          {/* El estado DEBAJO del botón, que no se mueve (ADR-2026-08-31 en Modelo). */}
          <p
            className={`text-[13px] px-1 leading-snug text-center ${
              errorTanda ? 'text-error-text' : 'text-black/50'
            }`}
          >
            {errorTanda ??
              falta ??
              (origenes.length === 1
                ? 'Una imagen por acabado. La cantidad la marcan los acabados elegidos.'
                : mismosAcabadosEnTodas
                  ? `${origenes.length} imágenes × ${acabadosDelPrimero.length} ${
                      acabadosDelPrimero.length === 1 ? 'acabado' : 'acabados'
                    }.`
                  : `${origenes.length} imágenes, cada una con sus acabados.`)}
          </p>
        </div>
      </>
    );

  /**
   * El pie de MOBILE: el MISMO `pie` de la columna de desktop, anclado abajo.
   *
   * No se rehace con `BarraInferiorMobile` —la barra de Modelo— porque ésta lleva un
   * contador de imágenes, y acá la cantidad no se elige: sale de los acabados. Los
   * ajustes son Calidad y Formato, que es justo lo que ya dibuja el pie de la columna, y
   * reusarlo es lo que hace que las dos pantallas digan lo mismo
   * (ADR-2026-09-03-modelo-mobile-toma-el-pie-de-desktop).
   */
  const pieMobile = (
    <div
      className="shrink-0 border-t border-black/[0.06] bg-white px-4 pt-3 flex flex-col gap-2.5"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
    >
      {pie}
    </div>
  );

  // La cabecera de la pantalla —migas + título— ADENTRO de la columna, en su borde de
  // arriba, como en Automático y "Mostrar cómo funciona". Es la misma en los dos pasos;
  // lo que cambia es el último nivel de las migas.
  const cabecera = (
    <div className="px-6 pt-5">
      <EncabezadoPantalla migas={migas} className="!px-0 !pt-0 !pb-2" />
      <h1
        key={paso}
        className="animate-paso-titulo text-[21px] font-semibold leading-tight text-brand-black"
      >
        {nombreModo}
      </h1>
    </div>
  );

  const claseColumna =
    'grow-0 shrink-0 flex flex-col bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden';

  // ── Paso 1: de dónde salen las fotos ──────────────────────────────────────
  // Las CREACIONES del usuario, no su catálogo de productos: el modo repinta una foto ya
  // lograda, y el packshot crudo es lo que menos ganas hay de repetir en ocho acabados
  // (ADR-2026-09-03-variantes-parte-de-una-imagen-no-de-un-producto). Arriba, subir una
  // desde la computadora — el mismo par que ofrece "Animar imagen".
  const columnaProducto = (
    <aside
      ref={panelConfigRef}
      style={{ flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }}
      className={claseColumna}
    >
      {cabecera}
      <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-3">
        <div className="flex-1 min-h-0 overflow-y-auto pb-4">
          <div className="mb-4">
            <p className="text-[17px] font-bold text-brand-black leading-tight">
              Elegí las imágenes
            </p>
            <p className="text-[13.5px] text-black/55 leading-relaxed mt-1">
              Las fotos que se van a repintar en cada acabado, con la misma forma y el
              mismo fondo. Podés elegir varias.
            </p>
          </div>
          {bloqueElegirImagen}
        </div>
        <div className="shrink-0 flex flex-col gap-3 pt-3 bg-white border-t border-black/[0.06]">
          {pie}
        </div>
      </div>
    </aside>
  );

  // ── Paso 2: la paleta ─────────────────────────────────────────────────────
  const columnaConfiguracion = (
    <aside
      ref={panelConfigRef}
      style={{ flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }}
      className={claseColumna}
    >
      {cabecera}
      <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-3">
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 pb-4">
          {cardOrigen}
          {paso === 'configurar' && (
            <>
              <div className="shrink-0 flex flex-col gap-3">{bloqueAcabados}</div>
              {bloqueInstruccion}
            </>
          )}
        </div>
        <div className="shrink-0 flex flex-col gap-3 pt-3 bg-white border-t border-black/[0.06]">
          {pie}
        </div>
      </div>
    </aside>
  );

  /**
   * Las tandas generadas. Sale de `columnaDerecha` porque en el teléfono no hay columna
   * de la derecha: van PRIMERAS en el scroll del paso 2, que es donde se las va a buscar
   * apenas se toca Generar (el mismo lugar que en "Ambiente completo").
   *
   * Tres por fila en la columna de desktop —lo que entra sin que la variante quede tan
   * chica que no se distinga del acabado de al lado— y DOS en el teléfono, donde el ancho
   * es un tercio del de esa columna.
   */
  const listaTandas = tandas.map((t) => {
    const total = t.porImagen.reduce((n, p) => n + p.acabados.length, 0);
    return (
      <section key={t.id} className="flex flex-col gap-2.5">
        <h4 className="text-[13px] font-semibold leading-none text-black/75">
          {total} {total === 1 ? 'imagen' : 'imágenes'}
          <span className="mx-[7px] inline-block h-[3px] w-[3px] rounded-full bg-[#3C3C43]/35 align-middle" />
          <span className="font-medium text-[#3C3C43]/50">Hace un momento</span>
        </h4>
        {/* Con varias fotos, el orden es por FOTO y después por acabado: la paleta de un
            mismo mueble queda junta, que es como se la mira. */}
        {t.error && (
          <p className="rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
            {t.error}
          </p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {t.celdas.map((c) => (
            <RecuadroVariante
              key={c.key}
              ratio={t.ratio}
              celda={c}
              origenUrl={t.porImagen.length > 1 ? c.origen.url : undefined}
            />
          ))}
        </div>
      </section>
    );
  });

  const columnaDerecha = (
    <div
      // Con resultados, la columna se sale de los paddings de la fila y llega al marco —
      // el mismo tratamiento que en los modos de `InterfazV2`
      // (ADR-2026-09-06-la-columna-de-resultados-llega-al-marco). El aire de adentro lo
      // devuelve el scroller del hook (`COLUMNA_RESULTADOS_AIRE_INTERNO`).
      className={`flex-1 min-w-0 flex flex-col overflow-hidden ${
        hayColumna ? COLUMNA_RESULTADOS_AL_BORDE : ''
      }`}
    >
      {/* La columna compartida en cuanto hay algo que mostrar —tandas de este modo, de
          otro, o el historial de la empresa—; si no, la muestra del modo, como siempre. */}
      {hayColumna ? (
        columna
      ) : (
        // La MUESTRA del modo hasta que haya algo generado
        // (ADR-2026-08-21-modelo-v2-la-muestra-se-queda).
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6">
          <div className="shrink-0 text-center max-w-[440px] mb-4">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              {TITULO_MUESTRA}
            </h2>
            <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
              {BAJADA_MUESTRA}
            </p>
          </div>
          {/* La imagen es HIJA DIRECTA de la columna, sin caja `flex-1` en el medio
              (ADR-2026-09-03-la-muestra-del-modo-es-un-bloque). */}
          <img
            src={PORTADA}
            alt="Ejemplo del modo Variantes de acabado"
            className="block min-h-0 w-auto max-w-[min(560px,100%)] max-h-full object-contain rounded-2xl"
          />
        </div>
      )}
    </div>
  );

  // ── Los paneles laterales ─────────────────────────────────────────────────

  /** El mismo par —subir o elegir creaciones— que la columna del paso 1. */
  const panelOrigen = (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-3">
        <p className="text-[12.5px] text-black/60 mb-4">
          Las fotos que se van a repintar en cada acabado, con la misma forma y el mismo
          fondo. Podés elegir varias.
        </p>
        {bloqueElegirImagen}
      </div>
      {/* El mismo pie que el panel de acabados, y por lo mismo: la selección es múltiple,
          así que elegir ya no cierra el panel y hace falta un lugar donde decir "listo" —
          diciendo antes la consecuencia de lo elegido. */}
      <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-3.5 border-t border-black/[0.06]">
        <p className="text-[13px] text-black/50">
          {origenes.length === 0
            ? 'Ninguna imagen elegida'
            : `${origenes.length} ${
                origenes.length === 1 ? 'imagen' : 'imágenes'
              } · ${cantidad} ${cantidad === 1 ? 'variante' : 'variantes'}`}
        </p>
        <button
          type="button"
          onClick={() => setPanelLateral(null)}
          className="h-10 px-5 rounded-2xl bg-brand-primary text-white text-[14px] font-semibold hover:opacity-90 transition-opacity"
        >
          Listo
        </button>
      </div>
    </div>
  );

  const panelAcabados = (
    <PanelAcabados
      catalogo={catalogoAcabados}
      elegidos={imagenEnEdicion ? (acabadosPorImagen[imagenEnEdicion] ?? []) : []}
      onAlternar={alternarAcabado}
      onSubirMuestra={() => inputMuestraRef.current?.click()}
      onListo={() => setPanelLateral(null)}
      // Sólo en "una por una": con el switch en "todas por igual" cada toque ya va a
      // todas, y el botón sería un atajo a lo que acaba de pasar.
      onAplicarATodas={
        origenes.length > 1 && !configIgualEnTodas ? aplicarAcabadosATodas : undefined
      }
      // A cuántas fotos le pega lo que se toca acá: a una editando una sola, a todas con
      // el switch en "por igual". El pie multiplica por esto para no decir "2 imágenes"
      // cuando en realidad salen 6.
      fotosAlcanzadas={configIgualEnTodas ? Math.max(origenes.length, 1) : 1}
      fondo={imagenEnEdicion ? (fondosPorImagen[imagenEnEdicion] ?? 'original') : 'original'}
      onFondo={elegirFondo}
      detalleFondo={imagenEnEdicion ? (detalleFondoPorImagen[imagenEnEdicion] ?? '') : ''}
      onDetalleFondo={escribirFondo}
      // Tres en el teléfono: el panel deja de ser una columna de 615px y pasa a ser la
      // pantalla, que mide la mitad.
      columnas={esEscritorio ? COLUMNAS_PALETA : 3}
    />
  );

  const panelFormato = (
    <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-3">
      <FormatPickerGrid
        selectedId={formatoId}
        onPick={(id) => {
          setFormatoId(id);
          setPanelLateral(null);
        }}
        columnas={COLUMNAS_PANEL_LATERAL}
      />
    </div>
  );

  const tituloPanel =
    panelLateral === 'origen'
      ? origenes.length > 1
        ? 'Imágenes de partida'
        : 'Imagen de partida'
      : panelLateral === 'acabados'
        ? // El título nombra la foto sólo cuando se está editando UNA. Con el switch en
          // "todas por igual" el panel gobierna todas, y decir "Imagen 2" ahí sería lo
          // contrario de lo que hace cada toque.
          origenes.length <= 1
          ? 'Acabados y fondo'
          : configIgualEnTodas
            ? `Acabados y fondo · Las ${origenes.length} imágenes`
            : `Acabados y fondo · Imagen ${
                origenes.findIndex((o) => o.key === imagenEnEdicion) + 1
              }`
        : 'Formato';

  // ── MOBILE: las dos pantallas ─────────────────────────────────────────────
  // Full-screen debajo del chrome flotante del shell (el back y el menú viven arriba, en
  // 3.5rem de alto más el safe area). Es UN contenedor y no dos portales: acá el modo es
  // la pantalla —no un sheet montado sobre otra—, así que no hay nada debajo que tapar.
  const pantallaMobile = (
    <div
      className="md:hidden fixed inset-0 z-30 bg-white flex flex-col"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {paso === 'presentacion' ? (
        // ── Pantalla 1: la presentación ─────────────────────────────────────
        // La MUESTRA del modo —qué hace, con un ejemplo— y abajo el camino para traer
        // imágenes, con la misma anatomía que "Ambiente completo" y que Automático
        // (ADR-2026-09-10-ambiente-completo-abre-con-la-muestra). Antes la muestra viajaba
        // arriba de la grilla, adentro del scroll del paso 1
        // (ADR-2026-09-10-la-muestra-del-modo-tambien-en-mobile): compartía pantalla con
        // una grilla que ya pedía elegir, y la explicación del modo se leía mientras se
        // scrolleaba para taparla.
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-4 pb-2">
            <div className="shrink-0 text-center max-w-[340px] mb-6">
              <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
                {TITULO_MUESTRA}
              </h2>
              <p className="text-[14px] text-[#3C3C43]/60 leading-snug mt-1">
                {BAJADA_MUESTRA}
              </p>
            </div>
            <img
              src={PORTADA}
              alt="Ejemplo del modo Variantes de acabado"
              className="block min-h-0 max-h-full w-auto max-w-[400px] object-contain rounded-2xl"
            />
          </div>
          {/* UN SOLO control, y es la CARD de Modelo (`CardProductos`) y no un botón
              lleno: la misma fila gris con el abanico de miniaturas y el chevron que abre
              lo que hay del otro lado. Va en su estado vacío —acá todavía no hay nada
              elegido—, así que muestra las últimas fotos del catálogo como muestra de que
              la biblioteca tiene algo adentro, igual que la de "Ambiente completo".

              Sin el "Subir nuevo producto" que Modelo pone al lado: la pantalla siguiente
              ya abre con la celda de subir como primera de la grilla
              (ADR-2026-09-04-subir-imagen-es-la-primera-celda-de-la-grilla), así que un
              segundo camino acá arriba ofrecería dos veces la misma cosa. Soltarle
              archivos encima también entra a esa pantalla, que es donde se sube. */}
          <div className="shrink-0 px-5 pt-4 pb-5 flex flex-col gap-2">
            {/* La card muestra lo ELEGIDO, no un vacío permanente: se vuelve acá con el
                back de los acabados, y una card que sigue diciendo "elegí" después de
                haber elegido tres fotos desmiente lo que pasó
                (ADR-2026-09-10-variantes-la-presentacion-muestra-lo-elegido). */}
            <CardProductos
              products={origenesComoCards}
              copyElegido="Cambiar imágenes"
              placeholders={
                miniaturasCatalogo.length > 0
                  ? miniaturasCatalogo
                  : PLACEHOLDERS_MUEBLES
              }
              onAbrirProductos={() => setPaso('producto')}
              onSubirArchivos={() => setPaso('producto')}
            />
            {/* Y con fotos elegidas, la salida hacia adelante: sin esto, volver acá
                dejaba como único camino reentrar a la grilla —una pantalla de selección
                ya resuelta— para tocar SU "Siguiente". */}
            {origenes.length > 0 && (
              <button
                type="button"
                onClick={() => setPaso('configurar')}
                className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              >
                Siguiente
              </button>
            )}
          </div>
        </div>
      ) : paso === 'producto' ? (
        <>
          {/* El título va ADENTRO del scroll y no clavado arriba: en un teléfono, fijo se
              come la mitad de la grilla, que es lo único que hay que hacer en este paso. */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-4">
            <div className="pt-1 pb-4">
              <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
                Elegí las imágenes
              </h2>
              <p className="text-[13.5px] text-black/55 leading-relaxed mt-1">
                Las fotos que se van a repintar en cada acabado, con la misma forma y el
                mismo fondo. Podés elegir varias.
              </p>
            </div>
            {bloqueElegirImagen}
          </div>
          {pieMobile}
        </>
      ) : paso === 'resultados' ? (
        // ── Pantalla 3: lo que salió ────────────────────────────────────────
        // Las variantes en una pantalla PROPIA, detrás del overlay de generación. Antes
        // la tanda se dibujaba arriba de las cards del paso 2: los ajustes seguían ahí
        // abajo, así que la pantalla era mitad resultado y mitad formulario, y las fotos
        // recién generadas se veían por la mitad. En desktop no hace falta —la tanda cae
        // en la columna de la derecha, al lado y no encima—; en el teléfono no hay
        // costado, y la única forma de darles la pantalla es dársela entera.
        <div className="flex-1 min-h-0 flex flex-col page-slide-in-right">
          <div className="shrink-0 px-5 pb-3">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              Tus variantes
            </h2>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-6">
            {listaTandas}
          </div>
          {/* Volver a los ajustes con lo elegido intacto: se llega igual con el back del
              chrome, pero el camino de ida —cambiar un acabado y generar de nuevo— es el
              que merece un botón. */}
          <div
            className="shrink-0 border-t border-black/[0.06] bg-white px-4 pt-3"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
          >
            <button
              type="button"
              onClick={() => setPaso('configurar')}
              className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center hover:opacity-90 transition-opacity"
            >
              Seguir generando
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col page-slide-in-right">
          {/* SIN "Volver" propio: el chrome flotante dibuja el suyo justo encima de esta
              fila y vuelve al paso 1 —ver el effect de `mobileBackOverride`—. */}
          <div className="shrink-0 px-5 pb-3">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              Elegí los acabados
            </h2>
          </div>
          {/* SIN la tanda arriba: lo generado vive en su propia pantalla
              (`resultados`). Acá quedan sólo los ajustes. */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-3">
            {cardOrigen}
            {bloqueAcabados}
            {bloqueInstruccion}
          </div>
          {pieMobile}
        </div>
      )}
    </div>
  );

  // Lo que en desktop se elige en el panel lateral, en el teléfono se elige en una capa
  // full-screen con EL MISMO contenido (`CapaModoMobile`). "Imágenes de partida" no está:
  // en mobile esa card devuelve al paso 1, que es esa misma grilla a pantalla completa.
  // El título de la capa es MÁS CORTO que el del panel de desktop: entre "Cancelar" y su
  // espejo quedan ~230px, y "Acabados y fondo · Imagen 2" entraba como
  // "Acabados y fondo · Image…". La foto se nombra igual —es lo que cambia entre una capa
  // y la otra—; lo que se recorta es "y fondo", que el propio contenido dice arriba de
  // todo con su título.
  const tituloAcabadosMobile =
    origenes.length <= 1
      ? 'Acabados y fondo'
      : configIgualEnTodas
        ? `Acabados · Las ${origenes.length}`
        : `Acabados · Imagen ${
            origenes.findIndex((o) => o.key === imagenEnEdicion) + 1
          }`;

  const capasMobile = (
    <>
      <CapaModoMobile
        abierta={panelLateral === 'acabados'}
        onCerrar={() => setPanelLateral(null)}
        titulo={tituloAcabadosMobile}
      >
        {panelAcabados}
      </CapaModoMobile>
      {/* Formato sí es un bottom sheet: es el mismo de toda la app y es una lista de
          opciones que commitea con el toque. */}
      <FormatPicker
        open={panelLateral === 'formato'}
        selectedId={formatoId}
        onChange={setFormatoId}
        onClose={() => setPanelLateral(null)}
      />
    </>
  );

  return (
    <>
      {!esEscritorio && (
        <>
          {pantallaMobile}
          {capasMobile}
          {/* El overlay de generación, el MISMO de Modelo y de Carrusel
              (`LoadingAnimation`): tapa la pantalla entera mientras se genera y deja al
              usuario en `resultados`. En desktop no se monta — allá la tanda aparece en la
              columna de la derecha y tapar la pantalla escondería justo lo que hay que
              mirar. */}
          {generandoMobile && (
            <LoadingAnimation
              message="Generando imágenes…"
              subMessage="Cada acabado tarda alrededor de medio minuto"
              durationMs={estimadoMobileRef.current}
              completeNow={completarMobile}
              onCompleted={() => {
                setGenerandoMobile(false);
                setCompletarMobile(false);
                setPaso('resultados');
              }}
            />
          )}
        </>
      )}
      <SubscriptionLimitModal
        isOpen={limitModal !== null}
        type={limitModal ?? 'plan-limit-reached'}
        onClose={() => setLimitModal(null)}
      />
          {/* ── DESKTOP: el marco de las pantallas de modo ─────────────────── */}
          {/* La MISMA geometría que "Mostrar cómo funciona" y "Ambiente completo": a ras
              del shell —`inset-y-0 right-0`, sin marco flotante ni borde—, sobre el gris de
              la app y redondeado sólo del lado de la sidebar. La tarjeta blanca alrededor
              de las dos columnas se sacó porque son dos cajas claras, una adentro de la
              otra (ADR-2026-09-03-el-modo-no-lleva-tarjeta-alrededor-de-las-columnas).

              `lg:pt-[52px]` es el aire de arriba de las pantallas del shell — el mismo que
              deja Automático, y lo que alinea el techo de la columna con el suyo. */}
          {esEscritorio && (
          <div
            // El visor de una creación es un lightbox portaleado al body: sin bajarle el z
            // a esta capa queda debajo y sólo se ve su velo (lo mismo que `cederZIndex` en
            // los modos que usan `ShellModoRedisenado`).
            className={`hidden md:flex fixed inset-y-0 right-0 ${
              visorAbierto ? 'z-0' : 'z-30'
            } bg-surface-mobile rounded-tl-[26px] rounded-bl-[26px] flex-col lg:pt-[52px]`}
            style={{ left: sidebarWidth }}
          >
            <div
              ref={areaRef}
              // `flex-row-reverse` + `justify-end`: la configuración a la IZQUIERDA y el
              // DOM con la presentación primero, como en los otros modos
              // (ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660).
              //
              // `FILA_MODO_REDISENO`: 52px de sangría a cada lado, el mismo aire que hay
              // arriba, en vez del 90% centrado que dejaba ~104px de un lado y la columna
              // sin llegar al borde (ADR-2026-09-06-la-fila-del-modo-arranca-a-52px-y-llega-al-borde).
              className={`relative flex-1 min-h-0 px-8 pb-6 flex flex-row-reverse justify-end gap-6 items-stretch ${FILA_MODO_REDISENO}`}
            >
              {columnaDerecha}
              {paso === 'producto' ? columnaProducto : columnaConfiguracion}

              <PanelLateralModo
                abierto={panelLateral !== null}
                onCerrar={() => setPanelLateral(null)}
                titulo={tituloPanel}
                maxAncho={ANCHO_PANEL_LATERAL}
                offsetIzquierdo={offsetPanelLateral}
                contenedorRef={areaRef}
              >
                {panelLateral === 'origen'
                  ? panelOrigen
                  : panelLateral === 'acabados'
                    ? panelAcabados
                    : panelFormato}
              </PanelLateralModo>
            </div>
          </div>
          )}

          {/* El input del muestra vive ACÁ y no adentro del panel: el panel se desmonta al
              cerrarse, y el diálogo de archivos del navegador no cuenta como "click
              adentro", así que elegir el archivo lo cerraba y el `onChange` caía en un
              input que ya no existía. */}
          <input
            ref={inputMuestraRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onArchivoMuestra}
          />

          {/* El de las imágenes de partida, por lo mismo: el panel lateral se desmonta al
              cerrarse y el diálogo de archivos no cuenta como click adentro. `multiple`
              porque la selección es múltiple: subir tres fotos tiene que ser una vuelta
              por el diálogo, no tres. */}
          <input
            ref={inputImagenRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={onArchivoImagen}
          />
    </>
  );
}

/**
 * ── EL PANEL DE ACABADOS ─────────────────────────────────────────────────────
 *
 * La paleta curada, en el panel lateral de siempre. Componente aparte —y no otro bloque
 * del render de arriba— porque tiene estado propio: cuál pastilla está activa, que sale
 * del SCROLL de la grilla y no de un filtro.
 *
 * "Mis muestras" abre la lista —es lo propio del usuario, y al final quedaba detrás de
 * todas las muestras curadas—. Con la sección vacía, lo primero que se ve al abrir el
 * panel es la card de subir y la regla de qué sirve subir.
 *
 * Por qué las pastillas navegan y no filtran: los ejes son cuatro y ninguno tiene más de
 * tres muestras (ADR-2026-09-23-los-ejes-de-variantes-son-cuatro), así que entran todos
 * en el mismo scroll. Filtrando, elegir una madera y un color eran dos filtros y dos
 * vueltas; así es un scroll, y la pastilla dice dónde estás parado. "Subir muestra" vive DENTRO de "Mis muestras" y no como un botón
 * aparte: subir una tela es elegir un acabado más, y lo subido queda al lado de lo curado.
 * Con la sección vacía es una zona a todo el ancho; con muestras cargadas, una celda más.
 */
const PanelAcabados: React.FC<{
  catalogo: Acabado[];
  elegidos: string[];
  onAlternar: (id: string) => void;
  onSubirMuestra: () => void;
  onListo: () => void;
  /**
   * Copiar lo elegido acá a todas las fotos. Sólo viene con dos o más: con una sola no
   * hay a quién copiárselo.
   */
  onAplicarATodas?: () => void;
  /** El fondo de ESTA foto — el concepto de "encuadre" se fue con él. */
  fondo: FondoId;
  onFondo: (id: FondoId) => void;
  /** El fondo descrito con palabras, para afinar la opción elegida. */
  detalleFondo: string;
  onDetalleFondo: (texto: string) => void;
  /** A cuántas fotos se aplica lo elegido acá — para la cuenta del pie. */
  fotosAlcanzadas?: number;
  /**
   * Columnas de la grilla de muestras. `COLUMNAS_PALETA` en el panel lateral de desktop;
   * menos en el teléfono, donde ese ancho es el de la pantalla y a cinco cada muestra
   * quedaba de 60px con el nombre truncado en dos letras.
   */
  columnas?: number;
}> = ({
  catalogo,
  elegidos,
  onAlternar,
  onSubirMuestra,
  onListo,
  onAplicarATodas,
  fondo,
  onFondo,
  detalleFondo,
  onDetalleFondo,
  fotosAlcanzadas = 1,
  columnas = COLUMNAS_PALETA,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const seccionesRef = useRef<Record<string, HTMLDivElement | null>>({});
  /**
   * Las pastillas de categoría. Se mide su alto —no se escribe— porque cuando viajan
   * adentro del scroll van `sticky` y tapan justo el techo al que apuntan `irA` y el
   * cálculo de la pastilla activa.
   */
  const pastillasRef = useRef<HTMLDivElement>(null);
  // La primera de la lista, sin nombrarla: el orden lo decide `CATEGORIAS_ACABADO` y
  // escribir acá cuál es lo desincronizaba en cuanto se movía una.
  const [categoriaActiva, setCategoriaActiva] = useState<CategoriaAcabadoId>(
    CATEGORIAS_ACABADO[0].id,
  );
  /**
   * Qué secciones están desplegadas. Colapsadas muestran UNA FILA
   * (ADR-2026-09-04-las-secciones-de-muestras-arrancan-en-una-fila): con las cinco
   * abiertas la grilla medía cuatro pantallas de scroll y el fondo —que vive arriba—
   * desaparecía a la primera rueda. Con tres acabados por eje hoy no se dispara casi
   * nunca; queda porque "Mis muestras" sí crece sin tope.
   */
  const [desplegadas, setDesplegadas] = useState<Record<string, boolean>>({});
  const alternarSeccion = (id: string) =>
    setDesplegadas((prev) => ({ ...prev, [id]: !prev[id] }));

  // La pastilla activa sale de qué sección está arriba de todo en el scroll. Se recalcula
  // en cada scroll y no con un IntersectionObserver por sección: son pocas secciones en
  // un contenedor chico, y el observer necesitaba un `rootMargin` afinado a mano para no
  // marcar dos a la vez.
  const onScroll = useCallback(() => {
    const cont = scrollRef.current;
    if (!cont) return;
    const techo =
      cont.getBoundingClientRect().top + (pastillasRef.current?.offsetHeight ?? 0) + 24;
    let actual: CategoriaAcabadoId = CATEGORIAS_ACABADO[0].id;
    for (const c of CATEGORIAS_ACABADO) {
      const el = seccionesRef.current[c.id];
      if (el && el.getBoundingClientRect().top <= techo) actual = c.id;
    }
    setCategoriaActiva(actual);
  }, []);

  const irA = (id: CategoriaAcabadoId) => {
    const el = seccionesRef.current[id];
    const cont = scrollRef.current;
    if (!el || !cont) return;
    // `scrollTop` a mano y no `scrollIntoView`: éste último scrollea también al ancestro
    // —el área de la pantalla del modo— y corre el panel entero unos píxeles.
    //
    // La distancia se mide con rects y NO con `offsetTop`: ése se mide contra el ancestro
    // POSICIONADO, que no siempre es este scroller —en la capa full-screen de mobile es la
    // capa, que arranca 48px más arriba— y la cuenta se iba de una sección entera. Con
    // rects da lo mismo quién esté posicionado.
    const top =
      el.getBoundingClientRect().top -
      cont.getBoundingClientRect().top +
      cont.scrollTop -
      // Con las pastillas pegajosas, el techo del scroller está TAPADO por ellas: sin
      // descontarlas, el título de la sección queda debajo.
      (pastillasRef.current?.offsetHeight ?? 0);
    cont.scrollTo({ top, behavior: 'smooth' });
    setCategoriaActiva(id);
  };

  const cantidad = elegidos.length;

  // ── EL FONDO ──────────────────────────────────────────────────────────────
  // Bloque propio, con su título, su explicación y su caja gris: pegado a las pastillas
  // de categoría se leía como un filtro más de las muestras
  // (ADR-2026-09-04-el-fondo-se-explica-y-se-describe). Es la OTRA decisión de la foto, y
  // ocupa el lugar de la card que se fue de la columna.
  const bloqueFondo = (
      <div className="shrink-0 pt-3">
        <div className="rounded-2xl bg-black/[0.028] p-3.5">
          <p className="text-[14px] font-bold text-brand-black leading-tight">Fondo</p>
          <p className="text-[12.5px] leading-snug text-black/55 mt-0.5">
            Dónde se ve el mueble. Es el mismo para todos los acabados de esta foto, así
            las variantes se comparan entre sí.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {FONDOS.map((f) => {
              const activo = f.id === fondo;
              const Icono = ICONO_FONDO[f.id];
              return (
                <button
                  key={f.id}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => onFondo(f.id)}
                  className={`relative rounded-xl overflow-hidden bg-white px-3 py-2.5 text-left transition-shadow ${
                    activo
                      ? 'ring-2 ring-inset ring-brand-primary'
                      : 'ring-1 ring-inset ring-black/[0.08] hover:ring-black/20'
                  }`}
                >
                  {/* El nombre ENVUELVE en vez de truncarse: en cuatro columnas
                      "Describir fondo" no entra en una línea, y "Describir fo…" no dice
                      nada. El grid iguala las alturas, así que la fila no se desalinea. */}
                  <span className="relative flex items-start gap-1.5">
                    <Icono
                      className={`w-4 h-4 shrink-0 mt-[1px] ${
                        activo ? 'text-brand-primary' : 'text-black/45'
                      }`}
                      strokeWidth={1.7}
                    />
                    <span className="min-w-0 text-[13px] font-semibold leading-tight text-brand-black">
                      {f.nombre}
                    </span>
                  </span>
                  {/* La descripción, ACÁ y no en un `title`: es lo que separa "de
                      estudio" de "un ambiente" y no se puede pedir que se adivine. */}
                  <span className="relative block text-[11.5px] leading-snug text-black/50 mt-1">
                    {f.descripcion}
                  </span>
                </button>
              );
            })}
          </div>
          {/* El campo se despliega con la cuarta opción y sólo con ella: es LA opción de
              escribir el fondo, y colgarlo también de las otras tres las convertía en
              medias opciones (ADR-2026-09-04-describir-fondo-es-la-cuarta-opcion). */}
          {fondo === 'describir' && (
            <div className="mt-2.5">
              <input
                value={detalleFondo}
                onChange={(e) => onDetalleFondo(e.target.value)}
                autoFocus
                placeholder="Un living con pared clara, piso de madera y una planta al lado…"
                className="w-full h-10 px-3.5 rounded-xl bg-white ring-1 ring-inset ring-black/[0.08] focus:ring-brand-primary/45 focus:outline-none text-[13px] text-brand-black placeholder:text-black/35 transition-shadow"
              />
            </div>
          )}
        </div>
      </div>
  );

  /* ── LAS MUESTRAS ──────────────────────────────────────────────────────── */
  const encabezadoMuestras = (
    <div className="shrink-0 pt-1">
      <p className="text-[14px] font-bold text-brand-black leading-tight">Acabados</p>
      <p className="text-[12.5px] leading-snug text-black/55 mt-0.5">
        Cada uno es una imagen del mismo producto. Elegí los que ofrecés.
      </p>
    </div>
  );

  /**
   * Categorías: llevan a su sección y marcan dónde estás.
   *
   * Van ADENTRO del scroll y `sticky`: lo que las precede —el Fondo y el título de
   * "Acabados"— scrollea con las muestras. Fuera del scroll quedaban flotando encima del
   * bloque de Fondo, como si lo filtraran.
   *
   * `shrink-0`, como todo lo que cuelga del scroller: es un flex column, y sin eso sus
   * hijos se COMPRIMEN cuando el contenido pasa el alto. La fila medía 24px —sólo su
   * padding— con las pastillas desbordándola, y el alto que `irA` descuenta salía de ahí,
   * así que el título de la sección terminaba tapado por ellas.
   */
  const pastillasCategorias = (
    <div
      ref={pastillasRef}
      // `-mx-6 px-6` para que el fondo blanco de la barra pegajosa llegue a los bordes
      // del panel y las muestras no se vean pasar por el costado.
      className="shrink-0 flex items-center gap-2 overflow-x-auto py-2 -mt-3 -mb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sticky top-0 z-10 bg-white -mx-6 px-6"
    >
      {CATEGORIAS_ACABADO.map((c) => {
        const activa = c.id === categoriaActiva;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => irA(c.id)}
            aria-current={activa ? 'true' : undefined}
            className={`shrink-0 rounded-full px-3.5 py-[7px] text-[13px] transition-colors ${
              activa
                ? 'bg-brand-primary text-white font-semibold'
                : 'bg-black/[0.045] text-brand-black/70 font-medium hover:bg-black/[0.08]'
            }`}
          >
            {c.nombre}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 flex flex-col gap-5"
      >
        {/* El Fondo y el título de "Acabados" viajan ADENTRO del scroll: sólo quedan
            clavadas las pastillas, que son la navegación. */}
        {bloqueFondo}
        {encabezadoMuestras}
        {pastillasCategorias}
        {CATEGORIAS_ACABADO.map((c) => {
          const items = catalogo.filter((a) => a.categoria === c.id);
          const esMias = c.id === 'mis-muestras';
          // Una categoría vacía no se dibuja — salvo "Mis muestras", que es donde vive el
          // botón de subir y por eso siempre está.
          if (items.length === 0 && !esMias) return null;
          // Colapsada, la sección muestra UNA FILA. En "Mis muestras" la card de subir
          // ocupa la primera celda, así que entra una muestra menos.
          const desplegada = !!desplegadas[c.id];
          const enUnaFila = columnas - (esMias ? 1 : 0);
          const hayMas = items.length > enUnaFila;
          const visibles = desplegada ? items : items.slice(0, enUnaFila);
          return (
            <div
              key={c.id}
              ref={(el) => {
                seccionesRef.current[c.id] = el;
              }}
              className="shrink-0 flex flex-col gap-2.5"
            >
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-[13px] font-semibold text-black/75 min-w-0 truncate">
                  {c.nombre}
                </h3>
                {hayMas && (
                  <button
                    type="button"
                    onClick={() => alternarSeccion(c.id)}
                    className="shrink-0 inline-flex items-center gap-0.5 text-[12.5px] font-semibold text-brand-primary hover:underline"
                  >
                    {desplegada ? 'Ver menos' : 'Ver todo'}
                    <ChevronDown
                      className={`w-3.5 h-3.5 shrink-0 transition-transform ${
                        desplegada ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                )}
              </div>
              {/* Con "Mis muestras" vacía, la regla de qué sirve subir va acá arriba, como
                  bajada de la sección: la celda de subir mide lo mismo que un acabado y no
                  tiene lugar adentro para tres renglones
                  (supersede ADR-2026-09-04-subir-muestra-es-una-zona-y-no-una-celda-con-texto-al-lado). */}
              {esMias && items.length === 0 && (
                <p className="-mt-0.5 text-[12.5px] leading-snug text-black/50 max-w-[420px]">
                  Una foto plana del material —una tela, un laminado— y se suma como un
                  acabado más.
                </p>
              )}
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))` }}
              >
                {esMias && (
                  // Subir es UNA CELDA MÁS de la grilla, del mismo tamaño que un acabado,
                  // con o sin muestras cargadas
                  // (ADR-2026-09-10-subir-muestra-es-una-celda-del-mismo-tamano). El texto
                  // va adentro, como la card de subir del paso 1. `self-start` para que no
                  // se estire al alto de la fila, que es más alta por la etiqueta que
                  // llevan las muestras debajo.
                  <button
                    type="button"
                    onClick={onSubirMuestra}
                    className="self-start aspect-square rounded-xl bg-brand-primary/[0.04] border-[1.5px] border-dashed border-brand-primary/35 hover:border-brand-primary/60 hover:bg-brand-primary/[0.07] grid place-items-center transition-colors"
                  >
                    <span className="flex flex-col items-center gap-1.5 px-1">
                      <span className="w-8 h-8 rounded-full bg-brand-primary/10 grid place-items-center text-brand-primary">
                        <Upload className="w-[17px] h-[17px]" />
                      </span>
                      <span className="text-[12px] font-semibold text-brand-primary leading-tight">
                        Subir muestra
                      </span>
                    </span>
                  </button>
                )}
                {visibles.map((a) => {
                  const activa = elegidos.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      aria-pressed={activa}
                      onClick={() => onAlternar(a.id)}
                      className="flex flex-col gap-1.5 text-center group"
                    >
                      <span className="relative block">
                        <Muestra
                          acabado={a}
                          className="w-full aspect-square rounded-xl ring-1 ring-inset ring-black/[0.08] group-hover:ring-black/20 transition-shadow"
                        />
                        {activa && (
                          <span className="absolute top-1.5 right-1.5 w-[22px] h-[22px] rounded-full bg-brand-primary text-white grid place-items-center ring-2 ring-white shadow-md">
                            <Check className="w-3 h-3" strokeWidth={3.5} />
                          </span>
                        )}
                      </span>
                      {/* El nombre ENVUELVE en vez de truncarse: en cinco columnas la
                          celda mide ~100px y "Laqueado mate" salía "Laqueado m…". La
                          etiqueta va abajo de la muestra, así que una fila con dos
                          renglones no desalinea las muestras. */}
                      <span
                        className={`text-[12px] leading-tight ${
                          activa
                            ? 'font-semibold text-brand-black'
                            : 'font-medium text-brand-black/75'
                        }`}
                      >
                        {a.nombre}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* El pie dice la CONSECUENCIA antes de cerrar el panel: cuántos acabados hay
          elegidos es cuántas imágenes van a salir. */}
      {/* En el teléfono el pie se APILA: la cuenta arriba y los botones abajo, a todo el
          ancho. En una fila, "Aplicar a todas" y "Listo" dejaban ~120px para la cuenta y
          las tres cosas quedaban partidas en dos renglones cada una. */}
      <div className="shrink-0 flex flex-col items-stretch gap-2 md:flex-row md:items-center md:justify-between md:gap-3 px-5 md:px-6 py-3 md:py-3.5 border-t border-black/[0.06]">
        <p className="text-[13px] text-black/50">
          {cantidad === 0
            ? 'Ningún acabado elegido'
            : `${cantidad} ${cantidad === 1 ? 'acabado' : 'acabados'} · ${
                cantidad * fotosAlcanzadas
              } ${cantidad * fotosAlcanzadas === 1 ? 'imagen' : 'imágenes'}`}
        </p>
        <div className="flex items-center gap-2 [&>button]:flex-1 md:[&>button]:flex-none">
          {onAplicarATodas && (
            <button
              type="button"
              onClick={onAplicarATodas}
              className="h-10 px-4 rounded-2xl text-brand-primary text-[14px] font-semibold hover:bg-brand-primary/[0.08] transition-colors"
            >
              Aplicar a todas
            </button>
          )}
          <button
            type="button"
            onClick={onListo}
            className="h-10 px-5 rounded-2xl bg-brand-primary text-white text-[14px] font-semibold hover:opacity-90 transition-opacity"
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  );
};
